import { Fragment, useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Search, Plus, ArrowUp, Heart, MessageCircle, Share2, MoreHorizontal, Megaphone, Lock, MapPin, X, UserPlus, UserCheck, Copy, Ban, Bell, Trash2, RefreshCw, Pin, TrendingUp , Download, Pencil } from 'lucide-react';
import { PressableCard } from './ui/PressableCard';
import { LikeButton } from './ui/LikeButton';
import { YapTopicLabel } from './ui/YapTopicLabel';
import { RoomShareCard } from './ui/RoomShareCard';
import { sameBracket } from '../utils/ageBracket';
import logoImage from '../assets/f736bb54a1ccb5a9a30d8b8ec9d3974d4b7ef9d6.png';
import logoImageWhite from '../assets/fac7d53f9fcb6f491ee2fb6641c18821c53c606e.png';
import goldIcon from '../assets/gold icon.png';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Input } from './ui/input';

import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { softDeleteYap, reportToWatchers } from '../services/WatcherService';
import { PresenceService, activityLabel } from '../services/PresenceService';
import { CreateYapModal } from './CreateYapModal';
import { ShareYapModal } from './ShareYapModal';
import { ShareShoutModal } from './ShareShoutModal';
import { ShoutYapModal } from './ShoutYapModal';
import { ShoutedYapOptionsModal } from './ShoutedYapOptionsModal';
import { DeleteYapModal } from './DeleteYapModal';
import { FlagImage } from './utils/countryFlags';
import { AnonymousAvatar, ANONYMOUS_USER } from './AnonymousAvatar';
import { useUserData } from './UserDataContext';
import { useNotificationBadges } from './NotificationContext';
import { useYapInteractions } from './YapInteractionsContext';
import { copyToClipboard } from './utils/clipboard';
import { toast } from 'sonner';
import { YapService, UserService, SearchService, ForumService, TribeService } from '../services/api';
import { formatTimeAgo } from './utils/timeFormat';
import { Yap, ShoutedYap, User } from '../types';
import { useSettings } from './SettingsContext';
import { ImageWithPreview } from './ImageWithPreview';
import { YapImageGrid } from './ui/YapImageGrid';
import { YapVideoPlayer } from './ui/YapVideoPlayer';
import { FriendBadge } from './ui/FriendBadge';
import { LikersModal } from './ui/LikersModal';
import { ParallaxBackground } from './utils/parallax';
import { useTheme } from './ThemeProvider';
import { EVENTS, SCREENS } from '../constants/navigation';
import { YapFilterModal } from './YapFilterModal';
import { type YapSortOption } from '../contexts/YapDayContext';
import { SlidersHorizontal } from 'lucide-react';
import { YapTabGuidelinesModal } from './YapTabGuidelinesModal';
import { SkeletonYap } from './ui/SkeletonPrimitives';
import { useScreenReady } from '../contexts/ScreenTransitionContext';
import { getRankRingParams, getRankBadge } from './utils/rankUtils';
import { LegionAvatarWrapper } from './LegionAvatarWrapper';
import { SafeStorage } from '../utils/safeStorage';
import { useSearchParams } from 'react-router-dom';
import { useYapDay } from '../contexts/YapDayContext';
import { useAuthGate } from '../contexts/AuthGateContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { useFeedHides } from '../hooks/useFeedHides';
import { sortByGroupTrending } from '../utils/groupTrending';
import { getYapAuthorInfo, getYapInteractionId, getYapPostType, getOriginalYapId, isQuoteShout, isShoutedYap } from './utils/yapUtils';
import { EmbeddedYap } from './EmbeddedYap';
import { LegionDeleteModal } from './LegionDeleteModal';
import { FormattedText } from './FormattedText';
import { VoiceReplyBubble } from './VoiceReplyBubble';
import { LinkPreview } from './LinkPreview';
import { PostMenu } from './ui/PostMenu';
import { PollCard } from './ui/PollCard';
import RankBadge from './RankBadge';
import { extractFirstUrl } from './utils/textParsers';
import { PromptComposer } from './PromptComposer';
import { EditPostModal } from './EditPostModal';
import { useGuide } from '../guide/useGuide';
import { ExpandableText } from './ui/ExpandableText';

interface YapTabScreenProps {
  initialTab?: 'world' | 'friends';
  onTabChange?: (tab: 'world' | 'friends') => void;
  groupId?: string;
  groupType?: 'forum' | 'tribe';
  groupName?: string;
}

// Fixed world-feed cutoff: the YapTab shows all non-deleted yaps created ON OR
// AFTER this date. This is a fixed anchor (not a rolling window) — the visible
// range grows over time (Feb 12 2026 → today) rather than sliding forward.
const WORLD_FEED_CUTOFF = '2026-02-12T00:00:00.000Z';

// "Trending" only ranks yaps from the last 7 days. Most Engagement covers the
// full range since WORLD_FEED_CUTOFF instead.
const TRENDING_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * How hard age pulls a yap down the Trending feed.
 *
 * Raised from 1.5, because at 1.5 engagement outran age badly enough that
 * Trending stopped being about what is happening now. Scores for the shapes
 * that actually occur, with engagement = likes + shouts*2 + comments*1.5:
 *
 *                               1.5      2.0
 *   brand new, no engagement    0.354    0.250
 *   1h old, 5 engagement        1.155    0.667
 *   24h old, 200 engagement     1.516    0.297
 *   72h old, 1000 engagement    1.573 <- 0.183
 *
 * At 1.5 the three-day-old viral post scored HIGHER than everything else on the
 * board, including a yap posted an hour ago, so one popular post could sit at
 * the top of Trending for days and new posts could not displace it.
 *
 * At 2.0 the hour-old yap leads, a genuinely hot 24-hour post still places above
 * a brand-new one, and nothing older than about two days can camp at the top.
 * Recency wins without the feed collapsing into Newest Yaps, which is the thing
 * to avoid at the other extreme: Trending has to stay distinct from the other
 * two sorts.
 *
 * Named rather than inline so the trade-off is one number to move. Higher is
 * more recency-biased.
 */
const TRENDING_GRAVITY = 2.0;

/**
 * Hours added to a yap's age before the decay is applied.
 *
 * Stops the score exploding for something posted seconds ago and flattens the
 * ordering among the newest few, so engagement still decides between them.
 */
const TRENDING_AGE_OFFSET_HOURS = 2;

// Logged-out visitors can preview this many world yaps before the "create an
// account to continue" wall blocks further scrolling.
const PUBLIC_PREVIEW_LIMIT = 10;

// How far a recently-interacted yap slides down the World feed. A demotion, not
// a banishment: untouched yaps take priority, but a yap the reader just liked
// drops about one screenful rather than below everything else loaded, so it is
// still a short scroll away instead of reading as having vanished.
const SINK_DEMOTION_SLOTS = 10;

// Incremental feed rendering: only mount this many yap cards at first, then load
// more as the user scrolls near the bottom. Keeps the DOM small so scrolling
// stays smooth instead of rendering hundreds of heavy cards at once.
const INITIAL_RENDER = 12;
const RENDER_STEP = 10;

// How many yaps (and shouts) are fetched per network page. The world feed is an
// open-ended range now, so it is walked a page at a time with a cursor instead
// of being capped at a single fixed fetch — anything past that cap used to be
// permanently unreachable. Smaller pages also make the first paint faster.
const FEED_PAGE_SIZE = 100;

// Bound on how many extra pages a single "load more" will pull while looking for
// items that survive the world-feed filter (a page can be entirely group posts).
const MAX_PAGES_PER_LOAD = 5;

// In-session cache of the world feed so returning to the tab (which remounts
// this screen) renders instantly — no network round-trip, no loading skeletons,
// no content shift under the restored scroll position. The live "New Yaps
// Dropped!" listener surfaces anything newer via the pill. Cleared on full
// page reload. Cursors ride along so a remount resumes paging where it left off
// instead of re-walking pages it already has.
let yapFeedCache: {
  yaps: Yap[];
  at: number;
  yapCursor: string | null;
  shoutCursor: string | null;
  yapHasMore: boolean;
  shoutHasMore: boolean;
} | null = null;

// Shape a raw shout into the feed-item form the renderers expect. Extracted from
// the initial fetch so paged loads build identical objects.
const mapShoutToFeedItem = (shout: any) => ({
  ...shout.originalYap,
  id: shout.id,
  likes: shout.likes || 0,
  comments: shout.comments || 0,
  metrics: shout.metrics || { likes: 0, comments: 0, shouts: 0, shares: 0 },
  originalYapId: shout.originalYapId || (shout.originalYap as any)?.id,
  originalYap: shout.originalYap,   // ← keep nested so renderers can use yap.originalYap
  author: shout.shoutedByName || 'User', // Override with shouter
  username: `@${(shout.shoutedByUsername || 'user').replace(/@/g, '')}`,
  shoutedByUsername: `@${(shout.shoutedByUsername || 'user').replace(/@/g, '')}`,
  avatar: shout.shoutedByAvatar || '',
  rank: shout.shoutedByRank || '',
  authorCountryFlag: shout.shoutedByCountryFlag || '',
  shoutedBy: shout.shoutedBy,
  shoutedByName: shout.shoutedByName || 'User',
  shoutedByAvatar: shout.shoutedByAvatar || '',
  shoutedByCountryFlag: shout.shoutedByCountryFlag,
  shoutedByResidence: shout.shoutedByResidence,
  shoutedByLocation: shout.shoutedByLocation,
  shoutCaption: shout.shoutCaption,
  shoutImages: shout.shoutImages,
  audioUrl: shout.audioUrl,
  duration: shout.duration,
  visibility: shout.visibility,
  // The shout's own flag, not the original's: the spread above is a snapshot
  // taken when it was shouted, so the original's flag would be stale here.
  hiddenFromPreview: shout.hiddenFromPreview === true,
  isShout: true,
  type: 'shouted' as const,
  time: shout.timestamp,
  sortTime: shout.timestamp,
});

// World feed excludes group posts unless they were explicitly cross-posted.
const passesWorldFilter = (yap: any) => {
  const hasGroupContext = yap.forumId || yap.tribeId || yap.topicId;
  if (!hasGroupContext) return true; // Native World yaps
  return yap.isCrossPosted === true;
};

// Newest-first by the feed's sort key.
const byNewestFirst = (a: any, b: any) =>
  new Date(b.sortTime || b.time || b.timestamp || 0).getTime() -
  new Date(a.sortTime || a.time || a.timestamp || 0).getTime();

export function YapTabScreen({ initialTab = 'world', onTabChange, groupId, groupType, groupName }: YapTabScreenProps) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showCreateYap, setShowCreateYap] = useState(false);
  const [showShareYap, setShowShareYap] = useState(false);
  const [showShareShout, setShowShareShout] = useState(false);
  const [showShoutModal, setShowShoutModal] = useState(false);
  const [showShoutOptionsModal, setShowShoutOptionsModal] = useState(false);
  const [selectedYapToShare, setSelectedYapToShare] = useState<Yap | null>(null);
  // Likers list for the user's own yaps (loaded async from the yap's likedBy roster).
  const [likers, setLikers] = useState<{ title: string; load: () => Promise<string[]> } | null>(null);
  const [selectedShoutToShare, setSelectedShoutToShare] = useState<any>(null);
  const [selectedYapToShout, setSelectedYapToShout] = useState<Yap | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [keywordSearch, setKeywordSearch] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // Moderate Delete State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingYap, setEditingYap] = useState<any>(null);
  const [yapToDelete, setYapToDelete] = useState<Yap | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Legion Moderation State
  const [legionDeleteTarget, setLegionDeleteTarget] = useState<{ yapId: string; content: string; authorId: string } | null>(null);

  // Legion Feed Pin State
  const [legionPinLoading, setLegionPinLoading] = useState<string | null>(null); // yapId being pinned/unpinned
  const [legionTrendingLoading, setLegionTrendingLoading] = useState<string | null>(null); // yapId being marked/unmarked trending

  const scrollViewportRef = useRef<HTMLDivElement>(null);

  // ── "Busiest yaps" recap view ─────────────────────────────────────────────
  //
  // Where the YapStats notification lands. Deliberately a URL parameter and not
  // a YapSortOption: activeSort is persisted, so adding a fourth option would
  // leave whoever tapped the notification with a permanently re-sorted feed.
  // Reading it from the URL means leaving the screen, or dismissing the banner,
  // puts everything back exactly as they had it.
  const [searchParams, setSearchParams] = useSearchParams();
  const recapMode =
    activeTab === 'world'
    && searchParams.get('sort') === 'most-replied'
    && searchParams.get('window') === '24h';

  const exitRecapMode = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('sort');
    next.delete('window');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  // The tour drives the scroll container itself; see the restoration effect.
  const { active: guideActive } = useGuide();
  const { friends, getFriendsCount, isFriend, addShout, shoutedYaps, removeShout, updateShoutCaption, userProfile, updateProfile, isTribeMember, isForumMember, blockUser } = useUserData();
  const { isBlockedContent, isBlockedId, isHiddenContent } = useContentFilter();
  const { isFeedHidden } = useFeedHides(lastRefreshTime);

  // Only blocked authors and muted words make a yap genuinely unrenderable, so
  // a page of nothing but those has found the reader nothing and the walk must
  // keep going. Recently-interacted yaps are deliberately NOT counted here: they
  // are demoted a few places, not removed, so they still fill the viewport and
  // counting them as invisible would page the collection for content already on
  // screen.
  const isUnrenderable = useCallback(
    (y: any) => isHiddenContent(y),
    [isHiddenContent],
  );

  // loadMoreFeed keeps a stable [groupId] dependency list and guards re-entrancy
  // with loadingMoreRef, so it must not close over a predicate that changes
  // identity whenever the blocked set does. Reading the latest predicate through
  // a ref keeps the callback stable while still filtering with current state.
  const isHiddenRef = useRef(isUnrenderable);
  useEffect(() => { isHiddenRef.current = isUnrenderable; }, [isUnrenderable]);
  const { unreadCount } = useNotificationBadges();
  const { toggleLike, toggleShout: toggleShoutGlobal, isYapLiked, isYapShouted, getLikeCount, getShoutCount, getPostReplyCount, isPostLiked, getPostLikeCount } = useYapInteractions();
  const { shareLocation } = useSettings();
  const { theme } = useTheme();
  const { currentYapDayStart, refreshYapDay, timeUntilReset, activeSort, setActiveSort, maybeResetSort } = useYapDay();
  const { isAuthed, requireAuth, openAuthPrompt } = useAuthGate();

  // Helper to determine if it's "Golden Hour" (last hour)
  const isGoldenHour = timeUntilReset.startsWith("00:");

  // Check membership for group context
  const isMember = (() => {
    if (!groupId) return true; // World/Friends tab - always allow interactions (subject to other rules)
    if (groupType === 'tribe') return isTribeMember(groupId);
    if (groupType === 'forum') return isForumMember(groupId);
    return false;
  })();

  const handleGroupInteractionCheck = () => {
    if (groupId && !isMember) {
      toast.error(`Members only`, { description: `Join this ${groupType} to interact.` });
      return false;
    }
    return true;
  };

  // Checks whether the current user may interact with a specific post.
  // Forum posts store topicId (not forumId) — both are handled here.
  const canInteractWithPost = (yap: Yap): boolean => {
    if (yap.tribeId && !isTribeMember(yap.tribeId)) return false;
    if (yap.forumId && !isForumMember(yap.forumId)) return false;
    if ((yap as any).topicId && !isForumMember((yap as any).topicId)) return false;
    return true;
  };

  const groupPostBlockMsg = (yap: Yap) => {
    if (yap.tribeId) return { title: 'Members only', desc: 'Join this tribe to interact.' };
    return { title: 'Members only', desc: 'Join this forum to interact.' };
  };

  // Fetch data using services
  const [allYaps, setAllYaps] = useState<Yap[]>(() => (!groupId && yapFeedCache) ? yapFeedCache.yaps : []);
  // Derived from the friends list instead of fetched.
  //
  // This used to be UserService.fetchAllUsers() — an unbounded
  // getDocs(collection(db,'users')) — awaited on every cold start of the YapTab,
  // pulling EVERY user document in the app with every field on it (bio,
  // portfolio arrays, identities, notification preferences) before the feed
  // could finish loading. It was the single most expensive thing on the startup
  // path, it grew with the user base, and since the age-bracket `list` rule
  // landed an unfiltered read of that collection is refused for non-admins
  // anyway, so it was frequently paying a round trip to be denied and fall back
  // to an empty array.
  //
  // Nothing needed all users. There are exactly three consumers and every one of
  // them is really asking "is this person a friend": the friends-tab shout check,
  // the friends-only visibility check, and the friends rail. A map of friends
  // plus the viewer answers all three IDENTICALLY — a non-friend was never found
  // in the old array either, it just cost a full collection read to not find them.
  const allUsers: User[] = useMemo(
    () => [...((friends ?? []) as unknown as User[]), userProfile as unknown as User]
      .filter(Boolean),
    [friends, userProfile],
  );
  const [isLoading, setIsLoading] = useState(() => !(!groupId && yapFeedCache));
  const isMountFetchRef = useRef(true); // first fetch of this mount → allowed to use the cache

  // ── "New Yaps Dropped!" indicator + cross-session scroll anchoring ─────────
  const [hasNewYaps, setHasNewYaps] = useState(false);
  const topFeedTsRef = useRef(0);          // ms timestamp of the newest yap currently shown
  const latestKnownTsRef = useRef(0);      // newest yap ts the live listener has reported
  const seenNewestRef = useRef(0);         // newest yap ts the user has already been taken to
  const scrolledDownRef = useRef(false);   // true when scrolled away from the top
  const lastScrollTopRef = useRef(0);      // latest scrollTop, persisted on navigation away
  const anchorRestoredRef = useRef(false); // cross-session anchor restore runs at most once
  const ANCHOR_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h staleness cutoff for a saved anchor
  // How many yap cards are mounted. Seeded from the saved scroll position when
  // there is one.
  //
  // The layout effect below restores scroll before paint, but it can only reach
  // a position the mounted cards actually span. Starting at INITIAL_RENDER meant
  // a deep saved offset was past scrollHeight, so the restore fell through to an
  // async retry that bumps visibleCount — and the browser painted at the top in
  // between. That is the flash of the feed jumping to the top and back.
  //
  // ~280px is a rough card height; being generous here only mounts a few extra
  // cards, while being short reintroduces the jump.
  const [visibleCount, setVisibleCount] = useState(() => {
    try {
      const saved = SafeStorage.getItem(`scroll_pos_yaptab_${initialTab}`);
      const px = saved ? parseInt(saved, 10) : 0;
      if (px > 0) return Math.max(INITIAL_RENDER, Math.ceil(px / 280) + RENDER_STEP);
    } catch { /* storage unavailable — the retry path still works */ }
    return INITIAL_RENDER;
  });
  const sortInitRef = useRef(false); // skip the on-mount run of the sort-change effect

  // ── World feed paging ─────────────────────────────────────────────────────
  // Yaps and shouts live in separate collections, so each keeps its own cursor
  // (an exclusive upper bound on `timestamp`) and its own end-of-collection flag.
  const yapCursorRef = useRef<string | null>((!groupId && yapFeedCache) ? yapFeedCache.yapCursor : null);
  const shoutCursorRef = useRef<string | null>((!groupId && yapFeedCache) ? yapFeedCache.shoutCursor : null);
  const yapHasMoreRef = useRef<boolean>((!groupId && yapFeedCache) ? yapFeedCache.yapHasMore : true);
  const shoutHasMoreRef = useRef<boolean>((!groupId && yapFeedCache) ? yapFeedCache.shoutHasMore : true);
  const loadingMoreRef = useRef(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreFeed, setHasMoreFeed] = useState(!groupId);

  // A warm return has a cached feed, so allYaps is already populated and the
  // screen is ready immediately; only a cold load holds the transition overlay.
  useScreenReady(!(isLoading && allYaps.length === 0));

  useEffect(() => {
    const fetchData = async () => {
      // setIsLoading(true); // Already true on mount or manual refresh
      try {
        if (!currentYapDayStart) {
          console.log('[YapTabScreen] Waiting for currentYapDayStart before fetching...');
          return;
        }

        // Fast path: on remount (tab switch) reuse the cached world feed instantly
        // instead of refetching. No skeletons, no network, no shift under the
        // restored scroll. Only the initial fetch of a mount may use the cache;
        // manual refresh / new-yap merges (which bump lastRefreshTime) always
        // re-fetch. Cold start (no cache) falls through to the full fetch.
        const isMountFetch = isMountFetchRef.current;
        isMountFetchRef.current = false;
        if (isMountFetch && !groupId && yapFeedCache) {
          setAllYaps(yapFeedCache.yaps);
          setHasMoreFeed(yapFeedCache.yapHasMore || yapFeedCache.shoutHasMore);
          setIsLoading(false);
          return;
        }

        let yaps: Yap[] = [];

        // Started here, awaited after the yaps page resolves. The two reads used
        // to be sequential — the whole yaps page had to come back before the
        // shouts request was even issued — which cost a full extra round trip on
        // every cold start for two queries that have nothing to do with each
        // other. Group feeds do not read shouts, so this stays null for them.
        const shoutPagePromise = groupId
          ? null
          : YapService.fetchShoutsPage(WORLD_FEED_CUTOFF, { pageSize: FEED_PAGE_SIZE });

        // World feed window: fixed cutoff — all yaps created on or after
        // WORLD_FEED_CUTOFF (Feb 12 2026). Not a rolling window; the range
        // grows over time. Replaces the previous YapDay-based daily cutoff.
        const feedSince = WORLD_FEED_CUTOFF;

        if (groupId) {
          // Fetch from group-specific collection with currentYapDayStart
          // (group/forum/tribe feeds keep their existing window — unchanged).
          if (groupType === 'forum') {
            yaps = await ForumService.getPosts(groupId, currentYapDayStart);
          } else if (groupType === 'tribe') {
            yaps = await TribeService.getPosts(groupId, currentYapDayStart);
          }
        } else {
          // World feed: first page only. Older yaps are pulled in by
          // loadMoreFeed as the reader approaches the end of what's loaded.
          const page = await YapService.fetchYapsPage(feedSince, { pageSize: FEED_PAGE_SIZE });
          yaps = page.yaps;
          yapCursorRef.current = page.cursor;
          yapHasMoreRef.current = page.hasMore;
        }

        // 🔒 SECURITY: Robust Filtering for Yap Tab.
        // Backstop against anything slipping past the query window. Group feeds
        // already applied their own (narrower) cutoff above, so the fixed world
        // cutoff here is permissive for them (leaves them unaffected) while
        // bounding the world feed to yaps created on or after WORLD_FEED_CUTOFF.
        const sinceDate = new Date(feedSince);
        const timerFilteredYaps = yaps.filter(yap => {
          const yapDate = new Date(yap.timestamp || yap.time || 0);
          return yapDate >= sinceDate;
        });

        if (timerFilteredYaps.length < yaps.length) {
          console.warn(`[YapTabScreen] Guard filtered out ${yaps.length - timerFilteredYaps.length} yaps older than ${feedSince}`);
        }

        // Filter / Map logic
        const finalYaps = groupId
          ? timerFilteredYaps // Group services already filter by topicId/tribeId
          : timerFilteredYaps.filter(passesWorldFilter); // World: group posts only if cross-posted

        // Apply group-specific trending sort for group feeds
        const sortedYaps = groupId
          ? sortByGroupTrending(finalYaps)
          : finalYaps; // World feed uses default sort (already sorted by time from Firestore)

        setAllYaps(sortedYaps);

        const shoutPage = shoutPagePromise
          ? await shoutPagePromise
          : { shouts: [], cursor: null, hasMore: false };
        const shouts = shoutPage.shouts as any[];
        if (!groupId) {
          shoutCursorRef.current = shoutPage.cursor;
          shoutHasMoreRef.current = shoutPage.hasMore;
          setHasMoreFeed(yapHasMoreRef.current || shoutPage.hasMore);
        }

        // Merge shouts into yaps for the feed
        const mergedShouts = shouts.map(mapShoutToFeedItem);

        // Merge and sort yaps and shouts. Filter out any yaps marked as deleted.
        const finalFeed = [...mergedShouts, ...sortedYaps]
          .filter(y => !y.deleted)
          .sort(byNewestFirst);

        setAllYaps(finalFeed);
        // Snapshot the world feed for instant remounts (tab switches), including
        // paging position so a remount doesn't re-walk pages already loaded.
        if (!groupId) {
          yapFeedCache = {
            yaps: finalFeed,
            at: Date.now(),
            yapCursor: yapCursorRef.current,
            shoutCursor: shoutCursorRef.current,
            yapHasMore: yapHasMoreRef.current,
            shoutHasMore: shoutHasMoreRef.current,
          };
        }

        // Clear local adjustments now that we have fresh server data
        window.dispatchEvent(new CustomEvent('refreshInteractions'));
      } catch (error) {
        console.error("Failed to fetch yaps/users", error);
      } finally {
        setTimeout(() => setIsLoading(false), 800);
      }
    };
    fetchData();

    // Listen for yap creation events to refresh feed
    const handleYapCreated = () => {
      fetchData();
    };
    window.addEventListener('yapCreated', handleYapCreated);

    // Listen for yap update events (e.g., new replies) to refresh feed
    const handleYapUpdated = () => {
      fetchData();
    };
    window.addEventListener('yapUpdated', handleYapUpdated);

    // Global YapDay reset: intentionally a no-op. The world feed now uses a
    // rolling 21-day window instead of a daily YapDay cycle, so there is no
    // daily feed-clear or "New Yap Day" toast. The YapDayContext event still
    // fires (data model/timer left intact) — we just ignore it here.
    const handleGlobalReset = () => {};
    window.addEventListener('GLOBAL_YAPDAY_RESET', handleGlobalReset);

    // Listen for immediate yap removal after Watcher soft-delete
    const handleYapDeleted = (e: CustomEvent) => {
      const { yapId } = e.detail || {};
      if (yapId) {
        setAllYaps(prev => prev.filter(y => y.id !== yapId));
      }
    };
    window.addEventListener('yapDeleted', handleYapDeleted as EventListener);

    return () => {
      window.removeEventListener('yapCreated', handleYapCreated);
      window.removeEventListener('yapUpdated', handleYapUpdated);
      window.removeEventListener('GLOBAL_YAPDAY_RESET', handleGlobalReset);
      window.removeEventListener('yapDeleted', handleYapDeleted as EventListener);
    };
  }, [currentYapDayStart, lastRefreshTime]); // Refetch when day changes or manual refresh

  // Pull-to-Refresh / Manual Sync handler
  const handleManualRefresh = () => {
    setIsLoading(true);
    refreshYapDay(); // Sync clock with server/global time logic
    setLastRefreshTime(Date.now()); // Trigger useEffect
  };

  // ── Load the next page of the world feed ──────────────────────────────────
  // Walks yaps and shouts backwards from their cursors and appends whatever
  // survives the world filter. A page can legitimately yield nothing visible
  // (e.g. it was all group posts), so it keeps pulling — bounded by
  // MAX_PAGES_PER_LOAD — rather than leaving the reader at a false end.
  const loadMoreFeed = useCallback(async () => {
    if (groupId) return;                       // group feeds are not paged
    if (loadingMoreRef.current) return;        // one in flight at a time
    if (!yapHasMoreRef.current && !shoutHasMoreRef.current) return;

    loadingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      const collected: any[] = [];
      let renderable = 0;
      let pages = 0;

      while (
        renderable === 0 &&
        pages < MAX_PAGES_PER_LOAD &&
        (yapHasMoreRef.current || shoutHasMoreRef.current)
      ) {
        pages++;

        const [yapPage, shoutPage] = await Promise.all([
          yapHasMoreRef.current && yapCursorRef.current
            ? YapService.fetchYapsPage(WORLD_FEED_CUTOFF, {
                pageSize: FEED_PAGE_SIZE,
                before: yapCursorRef.current,
              })
            : Promise.resolve({ yaps: [], hasMore: false, cursor: yapCursorRef.current }),
          shoutHasMoreRef.current && shoutCursorRef.current
            ? YapService.fetchShoutsPage(WORLD_FEED_CUTOFF, {
                pageSize: FEED_PAGE_SIZE,
                before: shoutCursorRef.current,
              })
            : Promise.resolve({ shouts: [], hasMore: false, cursor: shoutCursorRef.current }),
        ]);

        // Advance cursors even when a page yields nothing renderable.
        if (yapPage.cursor) yapCursorRef.current = yapPage.cursor;
        yapHasMoreRef.current = yapPage.hasMore;
        if (shoutPage.cursor) shoutCursorRef.current = shoutPage.cursor;
        shoutHasMoreRef.current = shoutPage.hasMore;

        const batch = [
          ...yapPage.yaps
            .filter(passesWorldFilter)
            .filter((y: any) => !y.deleted),
          ...(shoutPage.shouts as any[])
            .map(mapShoutToFeedItem)
            .filter((y: any) => !y.deleted),
        ];

        // Everything fetched goes into state, but only what this reader can
        // actually SEE counts as having found something.
        //
        // The loop exits as soon as it has a hit, so counting raw fetches meant a
        // page that was entirely blocked authors still ended the walk: the reader
        // saw no new content and nothing re-triggered a fetch. Counting renderable
        // items instead keeps the walk honest.
        //
        // But the filter must not decide what gets STORED. Recently-interacted
        // hides are World-tab-only and expire after 48h, so a yap dropped here
        // would also vanish from the Friends tab (both derive from allYaps) and
        // stay gone until a full refetch. Store everything; filter at derive time.
        collected.push(...batch);
        renderable += batch.filter((y: any) => !isHiddenRef.current(y)).length;
      }

      if (collected.length > 0) {
        setAllYaps(prev => {
          // Dedupe by id — a remount can replay a page already in state.
          const seen = new Set(prev.map((y: any) => String(y.id)));
          const additions = collected.filter(y => !seen.has(String(y.id)));
          if (additions.length === 0) return prev;
          const merged = [...prev, ...additions].sort(byNewestFirst);
          if (yapFeedCache) yapFeedCache.yaps = merged;
          return merged;
        });
      }

      const stillMore = yapHasMoreRef.current || shoutHasMoreRef.current;
      setHasMoreFeed(stillMore);
      if (yapFeedCache) {
        yapFeedCache.yapCursor = yapCursorRef.current;
        yapFeedCache.shoutCursor = shoutCursorRef.current;
        yapFeedCache.yapHasMore = yapHasMoreRef.current;
        yapFeedCache.shoutHasMore = shoutHasMoreRef.current;
      }
    } catch (e) {
      console.error('[YapTabScreen] Failed to load more of the feed', e);
    } finally {
      loadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [groupId]);

  // Muting a word (or blocking someone) shrinks the loaded feed in place, which
  // can leave too little content to fill the viewport — and with nothing left to
  // scroll, the usual "near the bottom" trigger never fires again. Top the feed
  // back up once whenever the rule set changes and the remaining count is thin.
  // loadingMoreRef inside loadMoreFeed guards against overlapping runs.
  const REFILL_THRESHOLD = 15;
  useEffect(() => {
    if (groupId) return;                       // group feeds are not paged
    if (!hasMoreFeed) return;
    // Trending was excluded here while its 7-day window discarded older pages.
    // It now continues into an "Older Yaps" section below the ranked set, so a
    // top-up has somewhere to land and the refill applies to it like any other
    // sort. (Recently-interacted yaps are sunk, not removed, so they no longer
    // thin that view out either.)
    const visible = allYaps.filter((y: any) => !isUnrenderable(y)).length;
    if (visible < REFILL_THRESHOLD) loadMoreFeed();
    // Intentionally keyed on the predicate, not on allYaps: this should run when
    // the mute/block/hide rules change, not on every feed mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnrenderable, groupId, hasMoreFeed, activeTab, activeSort]);


  // Sync activeTab with initialTab prop
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  // Helper function to check if location should be shown for a yap
  const shouldShowLocation = (yap: Yap) => {
    // Only show location if the yap author is the current user AND they have location sharing enabled
    return yap.username === userProfile.username && shareLocation && yap.country;
  };

  // Helper function to check if share/shout should be disabled for a yap
  const canShareOrShout = (yap: Yap) => {
    // 🔒 SECURITY: Group yaps (Tribe/Forum) cannot be shouted or shared to World
    if (yap.tribeId || yap.forumId || (yap as any).topicId) {
      return false;
    }
    // Anonymous yaps cannot be shouted
    if (yap.isAnonymous) {
      return false;
    }
    // Check if the yap itself is friends-only first
    // If yap is friends-only, can't share/shout regardless of profile privacy
    if (yap.visibility === 'friends') {
      return false;
    }
    // If yap is public, it can be shared/shouted
    return true;
  };

  // Helper function to check if anonymous yap has expired
  const isAnonymousYapExpired = (yap: Yap) => {
    if (!yap.isAnonymous || !yap.expiresAt) return false;
    const expiresAt = new Date(yap.expiresAt);
    return expiresAt.getTime() < Date.now();
  };

  // Helper function to get time remaining for anonymous yap
  const getTimeRemaining = (expiresAt: string | Date) => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diff = expiry.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h`;
    return `${minutes}m`;
  };


  // 24-hour refresh logic
  useEffect(() => {
    const checkRefresh = () => {
      const now = Date.now();
      const hoursSinceRefresh = (now - lastRefreshTime) / (1000 * 60 * 60);

      if (hoursSinceRefresh >= 24) {
        // Refresh yaps - in a real app this would fetch new data
        // For now, just update the timestamp
        setLastRefreshTime(now);
        toast.success('Yaps refreshed!', { duration: 2000 });
      }
    };

    // Check every minute
    const interval = setInterval(checkRefresh, 60000);
    return () => clearInterval(interval);
  }, [lastRefreshTime]);

  const scrollToTop = () => {
    if (scrollViewportRef.current) {
      scrollViewportRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCreateYap = () => {
    // Logged-out: prompt account creation instead of opening the composer.
    if (!requireAuth(() => handleCreateYapRef.current?.(), 'Create an account to post')) return;
    if (!handleGroupInteractionCheck()) return;
    setShowCreateYap(true);
  };

  const handleCreateYapRef = useRef(handleCreateYap);
  
  useEffect(() => {
    handleCreateYapRef.current = handleCreateYap;
  });

  useEffect(() => {
    const onOpenModal = () => handleCreateYapRef.current();
    // The guide opens the composer to point at Topics, Polls and Anonymous, and
    // has to be able to put it away again when it moves on or is skipped.
    // Closing this way discards whatever is in it: nothing is posted and no
    // draft is kept, which is what a tour that merely pointed at the thing
    // should leave behind.
    const onCloseModal = () => setShowCreateYap(false);
    window.addEventListener('openCreateYapModal', onOpenModal);
    window.addEventListener('guide:closePostModal', onCloseModal);
    return () => {
      window.removeEventListener('openCreateYapModal', onOpenModal);
      window.removeEventListener('guide:closePostModal', onCloseModal);
    };
  }, []);

  // Open the profile of whoever a feed item is attributed to (the shouter for a
  // shout, the author for a yap).
  //
  // The feed previously had NO working path to an author at all: the avatar and
  // name were inert, and the "View Profile" menu item dispatched
  // `navigateToUserYaps`, an event with no listener anywhere — so it silently did
  // nothing. navigateToFriendProfile is the event AppRouter actually handles, and
  // its handler already redirects to your own profile when the id is yours.
  const handleAuthorClick = (yap: any, authorInfo: any, authorId?: string, e?: React.MouseEvent) => {
    e?.stopPropagation();               // don't also open the yap via the card
    if (yap.isAnonymous) return;        // anonymous authors are not reachable
    if (!authorId) return;
    if (!requireAuth(undefined, 'Create an account to view profiles')) return;
    window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_FRIEND_PROFILE, {
      detail: {
        id: String(authorId),
        name: authorInfo?.name || '',
        username: String(authorInfo?.username || '').replace(/^@/, ''),
        image: authorInfo?.avatar || '',
      },
    }));
  };

  const handleYapClick = (yap: Yap) => {
    // Logged-out: opening a thread (Yap/Shout detail) is auth-gated — prompt signup.
    if (!requireAuth(undefined, 'Create an account to open this thread')) return;
    markYapSeen(yap); // read it → the pill shouldn't offer it again
    // Save scroll position before navigating
    if (scrollViewportRef.current) {
      const scrollPos = scrollViewportRef.current.scrollTop;
      console.log('[YapTabScreen] Saving scroll position:', scrollPos);
      SafeStorage.setItem(`scroll_pos_yaptab_${activeTab}`, scrollPos.toString());
    }

    // Include the current active tab in the navigation event so we can restore it on back
    const yapWithTab = {
      ...yap,
      sourceTab: activeTab, // Store which tab the user was on
    };

    // 🔒 DIFFERENTIATION: Shouts should go to ShoutDetail, Yaps to YapDetail
    if ((yap as any).type === 'shouted' || (yap as any).isShout) {
      console.log('[YapTabScreen] Navigating to Shout Detail:', yap.id);
      window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_SHOUT_DETAIL, { detail: yapWithTab }));
    } else {
      console.log('[YapTabScreen] Navigating to Yap Detail:', yap.id);
      window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_YAP_DETAIL, { detail: yapWithTab }));
    }
  };

  const handleFriendClick = (friend: User) => {
    window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_FRIEND_PROFILE, { detail: friend }));
  };

  const handleShareClick = (yap: Yap, e: React.MouseEvent) => {
    e.stopPropagation();
    markYapSeen(yap);
    setSelectedYapToShare(yap);
    setShowShareYap(true);
  };

  const handleToggleShout = (yap: Yap, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!requireAuth(undefined, 'Create an account to shout')) return;
    markYapSeen(yap);
    if (!handleGroupInteractionCheck()) return;

    // Check if yap is already shouted
    if (isYapShouted(yap.id)) {
      // Open shout options modal to manage existing shout
      setSelectedYapToShout(yap);
      setShowShoutOptionsModal(true);
    } else {
      // Open shout modal to create new shout
      setSelectedYapToShout(yap);
      setShowShoutModal(true);
    }
  };

  const handleShoutWithCaption = (caption: string, images: string[], friendsOnly: boolean, audioData?: { url: string, duration: number }) => {
    if (selectedYapToShout) {
      toggleShoutGlobal(selectedYapToShout.id, caption, images, audioData, friendsOnly);
      toast.success(friendsOnly ? 'Yap shouted to friends only!' : 'Yap shouted with your caption!', { duration: 1500 });
      // Refresh feed after Firestore write completes
      setTimeout(() => setLastRefreshTime(Date.now()), 2000);
    }
  };

  const handleShoutWithoutCaption = () => {
    if (selectedYapToShout) {
      toggleShoutGlobal(selectedYapToShout.id);
      toast.success('Yap shouted!', { duration: 1500 });
      // Refresh feed after Firestore write completes
      setTimeout(() => setLastRefreshTime(Date.now()), 2000);
    }
  };

  const handleDeleteCaption = () => {
    if (selectedYapToShout) {
      updateShoutCaption(selectedYapToShout.id, undefined, undefined);
      toast.success('Caption deleted', { duration: 1500 });
    }
  };

  const handleDeleteShout = () => {
    if (selectedYapToShout) {
      toggleShoutGlobal(selectedYapToShout.id);
      removeShout(selectedYapToShout.id);
      toast.success('Shout removed', { duration: 1500 });
      // Refresh feed after Firestore write completes to remove the shout
      setTimeout(() => setLastRefreshTime(Date.now()), 2000);
    }
  };

  const handleBlockUser = (authorId: string, authorName: string) => {
    // Blocking is per-account state — nothing to store for a logged-out visitor.
    if (!requireAuth(undefined, 'Create an account to block users')) return;
    try {
      blockUser(authorId);
      toast.success(`${authorName} has been blocked`);
    } catch (error) {
      console.error('Failed to block user:', error);
      toast.error(`Failed to block ${authorName}`);
    }
  };

  // Shared with the yap detail screen — see utils/downloadYap.
  const handleDownloadYap = async (yap: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const { downloadYapAsImage } = await import('../utils/downloadYap');
    await downloadYapAsImage(yap, { userProfile, getLikeCount, getShoutCount, getPostReplyCount });
  };

  const handleCopyYapLink = async (yapId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const success = await copyToClipboard(`https://afroyap.com/yap/${yapId}`);
    if (success) {
      toast.success('Yap link copied to clipboard!', { duration: 1500 });
    } else {
      toast.error('Failed to copy link');
    }
  };

  // Filter yaps based on active tab
  let filteredYaps = activeTab === 'friends'
    ? allYaps.filter((yap: any) => {
      // Never show soft-deleted/hidden yaps
      if (yap.deleted || yap.is_hidden) return false;
      // Anonymous yaps never appear in friends tab
      if (yap.isAnonymous) return false;

      const currentUserId = String(userProfile.id || '');
      const yapAuthorId = String(yap.userId || '');
      const shouterId = yap.type === 'shouted' ? (allUsers.find(u => u.username === yap.shoutedBy)?.id) : null;

      // Show if:
      // 1. Author is current user
      // 2. Author is a friend
      // 3. Shouter is current user
      // 4. Shouter is a friend
      const isMine = yapAuthorId === currentUserId || (yap.type === 'shouted' && (yap.shoutedByUsername === userProfile.username || yap.shoutedBy === currentUserId));
      const isFriendYap = isFriend(yapAuthorId) || (shouterId && isFriend(String(shouterId)));

      return isMine || isFriendYap;
    })
    : allYaps.filter((yap: any) => {
      // Never show soft-deleted/hidden yaps
      if (yap.deleted || yap.is_hidden) return false;

      // Shouts appear in the world timeline alongside yaps (they're already
      // merged into allYaps above, and were previously filtered back out here) —
      // but only QUOTE shouts, i.e. ones where the shouter added something of
      // their own (caption, image or voice note). A bare re-shout adds nothing to
      // the world feed except a duplicate of a post that's already in it.
      if (yap.isShout && !isQuoteShout(yap)) return false;

      // Filter out expired anonymous yaps
      if (isAnonymousYapExpired(yap)) return false;

      // World tab: Show public yaps + friends-only posts from friends
      if (yap.visibility === 'friends') {
        if (yap.isShout) {
          // A friends-only SHOUT is addressed to the shouter's friends, so match
          // on the shouter's uid. The username lookup below can't be used here:
          // a merged shout's `username` carries an "@" prefix and would never
          // match a user record, silently hiding every friends-only shout.
          const shouterId = String(yap.shoutedBy || '');
          return !!shouterId && (shouterId === String(userProfile.id) || isFriend(shouterId));
        }
        // Friends-only yaps are only visible to friends
        const yapUser = allUsers.find(u => u.username === yap.username);
        return yapUser && isFriend(String(yapUser.id));
      }
      // Show all non-friends yaps that are public, or yaps with no visibility set (default public)
      return !yap.visibility || yap.visibility === 'public';
    })
    // A room advert is filtered out entirely for the other age bracket, rather
    // than shown as an unopenable card. Yaps themselves are not bracket-gated,
    // so without this a minor would see adults' rooms listed in the feed even
    // though the room, its members and its messages are all closed to them.
    .filter(yap => {
      const share = (yap as any).roomShare;
      return !share?.roomId || sameBracket(userProfile, share.ageGroup);
    });

  // 🚫 HIDDEN CONTENT: applies to both tabs and to search results further down.
  // Covers blocked authors (yap author, anonymous authorId, shouter) and muted
  // words. loadMoreFeed already drops these while paging; this is the backstop
  // for everything that does not come through that path.
  filteredYaps = filteredYaps.filter((yap: any) => !isHiddenContent(yap));



  // ...
  const handleDeleteYapClick = (yap: Yap, e: React.MouseEvent) => {
    e.stopPropagation();
    setYapToDelete(yap);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (reason: string) => {
    if (!yapToDelete) return;
    setIsDeleting(true);
    try {
      await YapService.deleteYap(yapToDelete.id);
      toast.success("Yap deleted successfully");
      setShowDeleteModal(false);
      setYapToDelete(null);
      setLastRefreshTime(Date.now());
    } catch (error) {
      console.error("Failed to delete yap:", error);
      toast.error("Failed to delete yap");
    } finally {
      setIsDeleting(false);
    }
  };


  const [searchByKeywordResults, setSearchByKeywordResults] = useState<Yap[]>([]);
  const [searchByFriendResults, setSearchByFriendResults] = useState<Yap[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Handle Scroll Restoration — runs in a layout effect so the restore happens
  // before the browser paints (no visible jump from top → saved position).
  useLayoutEffect(() => {
    // The guide wins. It spotlights the composer at the top of the feed, and
    // restoring a saved position scrolled out from under the cut-out — two
    // things driving the same scroll container, with the restore retrying for
    // up to a second so it usually won. Anything that moves the page on its own
    // has to stand down while the tour is running.
    if (guideActive) return;
    if (!isLoading && allYaps.length > 0 && scrollViewportRef.current) {
      const savedPos = SafeStorage.getItem(`scroll_pos_yaptab_${activeTab}`);
      if (savedPos) {
        const targetPosition = parseInt(savedPos, 10);
        if (isNaN(targetPosition) || targetPosition <= 0) {
          SafeStorage.removeItem(`scroll_pos_yaptab_${activeTab}`);
          return;
        }

        console.log(`[YapTabScreen] Starting robust scroll restoration for ${activeTab} to ${targetPosition}`);
        
        let attempts = 0;
        const maxAttempts = 10;
        
        const tryScroll = () => {
          if (!scrollViewportRef.current) return;

          const currentContainer = scrollViewportRef.current;
          const maxScroll = currentContainer.scrollHeight - currentContainer.clientHeight;

          // With incremental rendering, not enough cards may be mounted to reach
          // the saved position — mount more and retry before giving up.
          const renderedCount = currentContainer.querySelectorAll('[data-yap-id]').length;
          if (targetPosition > maxScroll && renderedCount < displayYaps.length) {
            setVisibleCount((v) => Math.min(displayYaps.length, Math.max(v, renderedCount) + RENDER_STEP * 3));
            if (attempts < maxAttempts) { attempts++; setTimeout(tryScroll, 60); }
            return;
          }

          // Scroll as much as we can
          currentContainer.scrollTo(0, targetPosition);
          
          // If we reached the target or nearly did, we're done
          // Or if we've reached the maximum possible scroll height at this moment
          if (Math.abs(currentContainer.scrollTop - targetPosition) < 2 || (maxScroll > 0 && currentContainer.scrollTop >= maxScroll && targetPosition >= maxScroll)) {
            console.log(`[YapTabScreen] Scroll restoration successful on attempt ${attempts}`);
            SafeStorage.removeItem(`scroll_pos_yaptab_${activeTab}`);
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(tryScroll, 100); // Retry after a short delay
          } else {
            console.warn(`[YapTabScreen] Scroll restoration gave up after ${maxAttempts} attempts. Target: ${targetPosition}, Current: ${currentContainer.scrollTop}`);
            SafeStorage.removeItem(`scroll_pos_yaptab_${activeTab}`);
          }
        };

        tryScroll(); // immediate, pre-paint restore (cached content is already laid out)
        const timer = setTimeout(tryScroll, 50); // retry for late reflow (image loads, etc.)
        return () => clearTimeout(timer);
      }
    }
  }, [isLoading, allYaps, activeTab]);

  // Persist scroll position when leaving YapTab by ANY means — including a
  // BottomNav tab switch (which unmounts this screen), not just tapping into a
  // yap. Without this, switching to Discover and back reset the feed to the top.
  useEffect(() => {
    return () => {
      const c = scrollViewportRef.current;
      const live = c ? c.scrollTop : 0;
      // Prefer the live value, but fall back to the last onScroll value if the
      // container already reads 0 during the exit animation.
      const pos = live > 0 ? live : lastScrollTopRef.current;
      if (pos > 0) SafeStorage.setItem(`scroll_pos_yaptab_${activeTab}`, String(pos));
    };
  }, [activeTab]);

  // Track the newest yap timestamp currently shown — the baseline the live
  // listener compares against to decide whether genuinely new yaps exist.
  useEffect(() => {
    if (allYaps.length === 0) return;
    const newest = allYaps.reduce((max, y: any) => {
      const t = new Date(y.sortTime || y.time || y.timestamp || 0).getTime();
      return t > max ? t : max;
    }, 0);
    if (newest > 0) topFeedTsRef.current = newest;
  }, [allYaps]);

  // ── "Already seen the newest yap" watermark ───────────────────────────────
  // The pill is an offer to jump to something the user hasn't seen. Once they
  // take it (or scroll to the top themselves), that yap is spent — persisting
  // the timestamp stops the pill re-appearing for the same yap on a later
  // session, which it used to do whenever the saved scroll anchor was older
  // than the yap they'd already jumped to.
  useEffect(() => {
    const uid = String(userProfile?.id || '');
    if (!uid) return;
    const raw = SafeStorage.getItem(`yaptab_seen_newest_${uid}`);
    seenNewestRef.current = Number(raw) || 0;
  }, [userProfile?.id]);

  const persistSeen = useCallback((ts: number) => {
    if (!ts || ts <= seenNewestRef.current) return;
    seenNewestRef.current = ts;
    const uid = String(userProfile?.id || '');
    if (uid) SafeStorage.setItem(`yaptab_seen_newest_${uid}`, String(ts));
  }, [userProfile?.id]);

  const markNewestSeen = useCallback(() => {
    persistSeen(Math.max(topFeedTsRef.current || 0, latestKnownTsRef.current || 0));
  }, [persistSeen]);

  /**
   * Mark a single yap as seen because the user acted on it — opened its thread,
   * liked it, shouted it, shared it. The pill exists to say "there's something
   * new above you"; if the user has already engaged with that yap, offering to
   * jump to it is noise. Advancing the watermark only to THIS yap's timestamp
   * matters: engaging with an older yap must not suppress the pill for newer
   * ones the user still hasn't seen.
   */
  const markYapSeen = useCallback((yap: any) => {
    const ts = new Date(yap?.sortTime || yap?.time || yap?.timestamp || 0).getTime();
    if (!ts) return;
    persistSeen(ts);
    // If nothing newer than this yap is outstanding, the pill has nothing left
    // to offer — take it down rather than leaving it pointing at what they just read.
    const newestKnown = Math.max(topFeedTsRef.current || 0, latestKnownTsRef.current || 0);
    if (!newestKnown || ts >= newestKnown) setHasNewYaps(false);
  }, [persistSeen]);

  // Live listener → "New Yaps Dropped!" pill. Fires only on genuinely newer yaps
  // (create events); decay re-ranking never changes a yap's timestamp, so score
  // recomputes don't surface it. World feed + logged-in only.
  useEffect(() => {
    if (!isAuthed || activeTab !== 'world' || groupId) return;
    const unsub = YapService.watchNewestYap((newestIso) => {
      if (!newestIso || !topFeedTsRef.current) return;
      const newestMs = new Date(newestIso).getTime();
      latestKnownTsRef.current = Math.max(latestKnownTsRef.current, newestMs);
      if (newestMs <= topFeedTsRef.current) return; // nothing newer than what's shown
      if (scrolledDownRef.current) {
        if (newestMs <= seenNewestRef.current) return; // already jumped to this one
        setHasNewYaps(true);            // scrolled down → show pill, don't move content
      } else {
        setLastRefreshTime(Date.now()); // already at top → merge new yaps in directly
        markNewestSeen();               // they're looking at the top, so it counts as seen
      }
    // Own uid: the pill announces other people's posts, never your own.
    }, String(userProfile?.id || ''));
    return () => unsub();
  }, [isAuthed, activeTab, groupId, markNewestSeen, userProfile?.id]);

  /**
   * Puts the newest yap in the middle of the viewport rather than at the very
   * top. Landing at scrollTop 0 pins the new yap under the header, where it
   * reads as the top of a list rather than as the thing just arrived; centring
   * it puts it where the eye already is.
   *
   * Falls back to the top when the list has not rendered yet, so the tap always
   * moves somewhere rather than appearing to do nothing.
   */
  const centerNewestYap = () => {
    const container = scrollViewportRef.current;
    if (!container) return;
    const first = container.querySelector('[data-yap-id]') as HTMLElement | null;
    if (!first) { container.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    // Clamped at 0: when the card is taller than the viewport, or is near the
    // start of the list, the ideal centre is above the top of the scroll range
    // and a negative target would just be ignored.
    const target = offsetWithin(first, container) - Math.max(0, (container.clientHeight - first.offsetHeight) / 2);
    container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  };

  // Tapping the pill: dismiss, force a full feed reload, then centre the newest
  // yap. The scroll runs after the reload because the refreshed content changes
  // the list height, which would otherwise move the target out from under us.
  const handleNewYapsTap = () => {
    setHasNewYaps(false);
    markNewestSeen();                // spend this yap — the pill won't offer it again
    setActiveSort('newest');         // "show me what's new" → switch the active filter to Newest
    setVisibleCount(INITIAL_RENDER); // back to a small batch since we return to the top
    setLastRefreshTime(Date.now()); // full reload (bypasses the mount cache)
    setTimeout(centerNewestYap, 350);
  };

  // ── Cross-session scroll anchor (logged-in only) ──────────────────────────
  const offsetWithin = (el: HTMLElement, container: HTMLElement) =>
    el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;

  // Save the topmost visible yap (id + ms timestamp) so a full app close/reopen
  // can land the user back where they left off.
  const saveScrollAnchor = useCallback(() => {
    if (!isAuthed || activeTab !== 'world') return;
    const uid = String(userProfile?.id || '');
    const container = scrollViewportRef.current;
    if (!uid || !container) return;
    const cTop = container.getBoundingClientRect().top;
    const cards = Array.from(container.querySelectorAll('[data-yap-id]')) as HTMLElement[];
    const top = cards.find((el) => el.getBoundingClientRect().bottom > cTop + 4);
    const id = top?.getAttribute('data-yap-id');
    if (!id) return;
    const ts = Number(top!.getAttribute('data-yap-ts')) || 0;
    SafeStorage.setItem(`yaptab_anchor_${uid}`, JSON.stringify({ id, ts, savedAt: Date.now() }));
  }, [isAuthed, activeTab, userProfile?.id]);

  // Persist the anchor on background/close (native app-state + web visibility).
  useEffect(() => {
    if (!isAuthed) return;
    const onVisibility = () => { if (document.visibilityState === 'hidden') saveScrollAnchor(); };
    window.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', saveScrollAnchor);
    let capRemove: (() => void) | null = null;
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', ({ isActive }) => { if (!isActive) saveScrollAnchor(); });
        capRemove = () => { try { handle.remove(); } catch { /* noop */ } };
      } catch { /* web / plugin unavailable — visibility events cover it */ }
    })();
    return () => {
      window.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', saveScrollAnchor);
      if (capRemove) capRemove();
    };
  }, [isAuthed, saveScrollAnchor]);

  // Restore to the saved anchor on a cold start (once) unless it's stale (>24h)
  // or an in-app pixel restore is already queued for this tab.
  useEffect(() => {
    if (anchorRestoredRef.current) return;
    if (isLoading || allYaps.length === 0 || !isAuthed || activeTab !== 'world') return;
    const uid = String(userProfile?.id || '');
    if (!uid) return;
    anchorRestoredRef.current = true;
    if (SafeStorage.getItem(`scroll_pos_yaptab_${activeTab}`)) return; // in-app restore wins
    const raw = SafeStorage.getItem(`yaptab_anchor_${uid}`);
    if (!raw) return;
    SafeStorage.removeItem(`yaptab_anchor_${uid}`);
    let anchor: { id: string; ts: number; savedAt: number };
    try { anchor = JSON.parse(raw); } catch { return; }
    if (!anchor || !anchor.id) return;
    if (Date.now() - anchor.savedAt > ANCHOR_MAX_AGE_MS) return; // stale → land on top

    const scrollToAnchor = () => {
      const container = scrollViewportRef.current;
      if (!container) return;
      const cards = Array.from(container.querySelectorAll('[data-yap-id]')) as HTMLElement[];
      // Anchor yap gone (deleted / out of range) → nearest older yap by timestamp.
      const target = cards.find((el) => el.getAttribute('data-yap-id') === anchor.id)
        || cards.find((el) => (Number(el.getAttribute('data-yap-ts')) || 0) <= anchor.ts)
        || null;
      if (target) container.scrollTo(0, offsetWithin(target, container));
    };
    // With incremental rendering the anchor card may not be mounted yet — mount
    // enough cards to include it, then scroll.
    const idx = displayYaps.findIndex((y: any) => y.id === anchor.id);
    if (idx >= visibleCount) {
      setVisibleCount(Math.min(displayYaps.length, idx + 5));
      setTimeout(scrollToAnchor, 90);
    } else {
      setTimeout(scrollToAnchor, 60);
    }

    // Newer yaps arrived above the anchor while closed → offer the choice
    // (jump up vs stay) instead of silently showing them.
    const newestMs = allYaps.reduce((max, y: any) => {
      const t = new Date(y.sortTime || y.time || y.timestamp || 0).getTime();
      return t > max ? t : max;
    }, 0);
    // …but only for yaps they haven't already been offered and jumped to, so
    // reopening the app doesn't resurface the same "New Yaps Dropped!" pill.
    if (newestMs > anchor.ts && newestMs > seenNewestRef.current) setHasNewYaps(true);
  }, [isLoading, allYaps, isAuthed, activeTab, userProfile?.id]);

  // ── Rolling 24h Trending reset ────────────────────────────────────────────
  // Checked when YapTab mounts and again whenever the app comes back to the
  // foreground, so a session left open past the window still snaps back to
  // Trending the next time the user actually looks at the feed.
  useEffect(() => {
    maybeResetSort();
    const onVisible = () => { if (!document.hidden) maybeResetSort(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [maybeResetSort]);

  // Reset how many cards are mounted when the effective list changes (tab
  // switch or a new search) so we start from a small, fast batch again.
  useEffect(() => {
    setVisibleCount(INITIAL_RENDER);
  }, [activeTab, keywordSearch, searchQuery]);

  // On a sort change, re-render only a small batch from the top instead of
  // re-sorting/re-rendering every mounted card — that synchronous reorder is
  // what made switching filters feel slow. Also scroll to the top so the user
  // sees the top of the new ordering. Skipped on first mount to avoid fighting
  // scroll restoration.
  useEffect(() => {
    if (!sortInitRef.current) { sortInitRef.current = true; return; }
    setVisibleCount(INITIAL_RENDER);
    scrollToTop();
  }, [activeSort]);

  // Debounced search logic
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    const term = activeTab === 'world' ? keywordSearch : searchQuery;

    if (term.trim().length > 1) {
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const results = await SearchService.searchYaps(term);
          // Filter results to the fixed world-feed cutoff (matches the feed).
          const dayStart = new Date(WORLD_FEED_CUTOFF).getTime();
          const filteredResults = results.filter(y => {
            const yapTime = new Date(y.time || y.timestamp || 0).getTime();
            return yapTime >= dayStart;
          });

          if (activeTab === 'world') {
            setSearchByKeywordResults(filteredResults);
          } else {
            setSearchByFriendResults(filteredResults.filter(y => isFriend(String(y.userId))));
          }
        } catch (e) {
          console.error("Yap search failed:", e);
        }
      }, 500);
    } else {
      setSearchByKeywordResults([]);
      setSearchByFriendResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [keywordSearch, searchQuery, activeTab, isFriend]);

  // Combined yaps: Use search results if searching, otherwise use default filtered yaps
  let displayYaps = activeTab === 'world'
    ? (keywordSearch.trim().length > 1 ? searchByKeywordResults : filteredYaps)
    : (searchQuery.trim().length > 1 ? searchByFriendResults : filteredYaps);

  // Search results come straight from SearchService and skip the feed filters
  // above, so blocked authors and muted words have to be dropped here too.
  displayYaps = displayYaps.filter((y: any) => !isHiddenContent(y));

  // Apply sorting: Friends tab is always chronological (newest first). World tab uses activeSort.
  const effectiveSort = activeTab === 'friends' ? 'newest' : activeSort;

  // Combined engagement signal, shared by both ranked sorts so "Trending" and
  // "Most Engagement" agree on what engagement means. Shouts and comments carry
  // more weight than a like because they cost more effort.
  const getEngagement = (yap: any) => {
    const likes    = getLikeCount(yap.likes || 0, yap.id);
    const shouts   = getShoutCount(yap.shouts || 0, yap.id);
    const comments = yap.comments || 0;
    return likes + (shouts * 2) + (comments * 1.5);
  };

  // 📌 LEGION FEED PIN: Floats pinnedToFeed yap(s) above all other sorted content.
  // A pin only lasts 24h from when it was set (pinnedToFeedAt); after that the
  // yap falls back to its normal ranked position. Legacy pins with no timestamp
  // stay pinned until explicitly unpinned.
  //
  // Declared up here rather than beside the float itself because the Trending
  // split below has to know about pins: a pinned yap is floated to the very top
  // deliberately, so it must count as part of the ranked section even when it is
  // older than the window. Otherwise it would be the one item above the divider
  // that belongs below it, and the feed would appear to have two boundaries.
  const isPinActive = (y: any): boolean => {
    if (y.pinnedToFeed !== true) return false;
    const at = y.pinnedToFeedAt;
    const ms = at?.toDate ? at.toDate().getTime()
      : (at?.seconds ? at.seconds * 1000
      : (typeof at === 'string' ? new Date(at).getTime()
      : (typeof at === 'number' ? at : 0)));
    if (!ms) return true; // legacy pin (no timestamp) → keep pinned
    return (Date.now() - ms) < 24 * 60 * 60 * 1000;
  };

  // Trending ranks the last 7 days by decay score, then CONTINUES into older
  // yaps instead of dead-ending.
  //
  // The window used to be a filter: anything older than 7 days was dropped, the
  // feed stopped at whatever survived, and paging was disabled because every
  // page fetched would be discarded. It is now a SPLIT. The window still decides
  // what can be ranked as trending (nothing old rides in on engagement alone —
  // that is what Most Engagement is for), but it no longer decides what exists.
  // Older yaps follow the ranked set in plain reverse-chronological order under
  // an "Older Yaps" divider, fetched by the same loadMoreFeed as every other
  // view, back to WORLD_FEED_CUTOFF.
  //
  // The split is on timestamp, so a yap belongs to exactly one section by
  // construction: the two can never show the same yap. Both sections are derived
  // from displayYaps AFTER the world/blocking/muted/hidden/age filters above, so
  // the older section inherits every one of them for free.
  //
  // Not applied to keyword search (the reader went looking for it) or the recap
  // (windowed to 24h by design).
  const trendingSplitActive =
    effectiveSort === 'trending'
    && activeTab === 'world'
    && keywordSearch.trim().length <= 1
    && !recapMode;
  const trendingWindowStart = Date.now() - TRENDING_WINDOW_MS;
  /** Segment key: 0 = ranked "trending" section, 1 = "Older Yaps" section. */
  const trendingSegment = (y: any): number => {
    if (!trendingSplitActive) return 0;
    if (isPinActive(y)) return 0;
    const ms = new Date(y.sortTime || y.time || y.timestamp || 0).getTime();
    return ms < trendingWindowStart ? 1 : 0;
  };

  // ── Recently-interacted demotion ──────────────────────────────────────────
  // A yap the reader liked, shared, replied to, shouted or voted on in the last
  // 48h gives up its place to yaps they have NOT touched, so coming back leads
  // with new material. It is demoted by a bounded number of slots, never removed
  // and never floored to the end of the list: a yap pushed below everything else
  // loaded is indistinguishable from one that vanished.
  // Deliberately NOT applied to:
  //   • the Friends tab — engaging with a friend's post is no reason to bury it,
  //     which is the opposite of what that tab is for;
  //   • keyword search — the reader went looking for it;
  //   • their own yaps — handled inside isFeedHidden.
  // Only collected here; the demotion happens after the sort below, so the feed
  // keeps its normal Trending / Newest / Most Engagement order underneath it.
  const sinkApplies =
    activeTab === 'world' && !groupId && keywordSearch.trim().length <= 1;
  const sunkIds = new Set<string>();
  if (sinkApplies) {
    for (const y of displayYaps) {
      if (isFeedHidden(y)) sunkIds.add(String(y.id));
    }
  }

  displayYaps = [...displayYaps].sort((a: any, b: any) => {
    switch (effectiveSort) {
      case 'trending': {
        // The whole ranked section sits above the whole older section, and the
        // older section is plain newest-first among itself — decay ranking is
        // meaningless there, because everything in it has already decayed past
        // the window.
        const segDiff = trendingSegment(a) - trendingSegment(b);
        if (segDiff !== 0) return segDiff;
        if (trendingSegment(a) === 1) {
          return new Date(b.sortTime || b.time || 0).getTime() - new Date(a.sortTime || a.time || 0).getTime();
        }
        // Time-decayed score so BOTH brand-new yaps and high-engagement yaps
        // surface near the top simultaneously (Hacker News style gravity):
        // engagement lifts a yap up, age pulls it down, so fresh posts get
        // visibility without burying popular ones.
        const getTrendingScore = (yap: any) => {
          const ageHours = Math.max(0, (Date.now() - new Date(yap.sortTime || yap.time || yap.timestamp || 0).getTime()) / 3600000);
          return (getEngagement(yap) + 1) / Math.pow(ageHours + TRENDING_AGE_OFFSET_HOURS, TRENDING_GRAVITY);
        };
        return getTrendingScore(b) - getTrendingScore(a);
      }
      case 'most-engagement': {
        // Pure engagement over the full feed range (since WORLD_FEED_CUTOFF) —
        // no decay, so an old high-engagement yap can hold the top spot
        // indefinitely. Ties break newest-first so the order stays stable.
        const diff = getEngagement(b) - getEngagement(a);
        if (diff !== 0) return diff;
        return new Date(b.sortTime || b.time || 0).getTime() - new Date(a.sortTime || a.time || 0).getTime();
      }
      case 'newest':
      default:
        return new Date(b.sortTime || b.time || 0).getTime() - new Date(a.sortTime || a.time || 0).getTime();
    }
  });

  // Hand priority to untouched yaps by demoting the interacted ones a fixed
  // number of places. Runs after the sort, so a liked yap cannot score its way
  // back to the top.
  //
  // Done as a stable re-sort on (position + penalty) rather than by moving items
  // to the end: each touched yap drops at most SINK_DEMOTION_SLOTS places, the
  // untouched keep their ranked order, and a reader who has liked most of what
  // is loaded still gets a full feed instead of a top-heavy empty one. Ties go
  // to the untouched yap, which is the whole point of the pass.
  if (sunkIds.size > 0) {
    const demote = (list: any[]) => list
      .map((y: any, i: number) => {
        const sunk = sunkIds.has(String(y.id));
        return { y, sunk, rank: i + (sunk ? SINK_DEMOTION_SLOTS : 0) };
      })
      .sort((a, b) => (a.rank - b.rank) || (Number(a.sunk) - Number(b.sunk)))
      .map((entry) => entry.y);

    if (trendingSplitActive) {
      // Run per segment. A single pass over the joined list could push a yap
      // from the last 10 ranked slots down past the divider, which would put it
      // under an "Older Yaps" heading while being less than 7 days old — and
      // would also move the divider itself, since its position is derived from
      // the segment of the item at each index.
      displayYaps = [
        ...demote(displayYaps.filter((y: any) => trendingSegment(y) === 0)),
        ...demote(displayYaps.filter((y: any) => trendingSegment(y) === 1)),
      ];
    } else {
      displayYaps = demote(displayYaps);
    }
  }

  // Busiest-first, over the recap's own 24h window.
  //
  // Replaces the ordering entirely rather than layering on it: the reader
  // followed a notification that said what happened today, so freshness and
  // the recently-interacted demotion are not what they came for. Ties break
  // newest-first so the order is stable between renders.
  if (recapMode) {
    const since = Date.now() - 24 * 60 * 60 * 1000;
    displayYaps = displayYaps
      .filter((y: any) => new Date(y.sortTime || y.time || y.timestamp || 0).getTime() >= since)
      .sort((a: any, b: any) => {
        const diff = (Number(b.comments) || 0) - (Number(a.comments) || 0);
        if (diff !== 0) return diff;
        return new Date(b.sortTime || b.time || 0).getTime() - new Date(a.sortTime || a.time || 0).getTime();
      });
  }

  const pinnedFeedYaps = displayYaps.filter((y: any) => isPinActive(y));
  const unpinnedFeedYaps = displayYaps.filter((y: any) => !isPinActive(y));
  displayYaps = [...pinnedFeedYaps, ...unpinnedFeedYaps];

  // Where the "Older Yaps" divider goes: the first item that is past the
  // trending window. Resolved here, after every reordering above, so it can
  // never disagree with the list it is dividing. -1 means no divider, which
  // covers every other sort and the case where nothing older has loaded yet.
  const olderYapsStartIndex = trendingSplitActive
    ? displayYaps.findIndex((y: any) => trendingSegment(y) === 1)
    : -1;

  // Public preview cap: logged-out visitors see at most PUBLIC_PREVIEW_LIMIT world
  // yaps, then the account wall (rendered after the list). Friends tab is
  // auth-gated separately, so this only applies to the world feed.
  const showPreviewWall = !isAuthed && activeTab === 'world';
  if (showPreviewWall) {
    // Moderators curate the preview: flagged yaps are skipped (the next one
    // takes the slot) rather than counted against the limit.
    displayYaps = displayYaps
      .filter((y: any) => y.hiddenFromPreview !== true)
      .slice(0, PUBLIC_PREVIEW_LIMIT);
  }

  // Auto-paging is only meaningful for views that actually reach backwards.
  // Skipped for: group feeds (not paged), the preview wall (capped by design),
  // and keyword search (served by SearchService, not this list).
  //
  // Trending used to be excluded here, because its 7-day window discarded every
  // page a fetch could return. Now that the window splits the feed instead of
  // truncating it, older pages have somewhere to go: they land under the
  // "Older Yaps" divider. So Trending pages like every other sort, and reaches
  // WORLD_FEED_CUTOFF the same way.
  const feedPagingEnabled =
    !groupId &&
    !showPreviewWall &&
    keywordSearch.trim().length <= 1 &&
    // The recap is windowed to 24 hours, so paging backwards can only return
    // yaps it will discard.
    !recapMode;

  // Map friend objects to actual user objects from allUsers (friends is AppUser[], not string[])
  const unsortedFriendsList = allUsers.filter(user => friends.some(f => String(f.id) === String(user.id)));

  // Last-active times for the friends strip, read through the same rule-gated
  // status doc the activity label uses. A friend who hides their activity (or
  // a viewer who hides their own) reads as null and sorts after everyone with a
  // time, so the order never reveals more than the label would.
  const friendIdsKey = unsortedFriendsList.map(f => String(f.id)).join(',');
  const [friendLastActive, setFriendLastActive] = useState<Record<string, number | null>>({});
  useEffect(() => {
    if (activeTab !== 'friends' || !friendIdsKey) return;
    let cancelled = false;
    const ids = friendIdsKey.split(',');
    // Resolved together and applied once, so the strip reorders a single time
    // rather than shuffling as each read lands.
    Promise.all(ids.map(id => PresenceService.getLastActive(id))).then(times => {
      if (cancelled) return;
      setFriendLastActive(Object.fromEntries(ids.map((id, i) => [id, times[i]])));
    });
    return () => { cancelled = true; };
  }, [activeTab, friendIdsKey]);

  const friendsList = useMemo(() => {
    const at = (id: string) => friendLastActive[id] ?? 0;
    // Array.prototype.sort is stable, so friends with no readable time keep
    // their existing relative order.
    return [...unsortedFriendsList].sort((a, b) => at(String(b.id)) - at(String(a.id)));
  }, [unsortedFriendsList, friendLastActive]);

  // Filter friends scrollbar by search query
  const filteredFriendsList = searchQuery.trim()
    ? friendsList.filter(f =>
        f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (f.username || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : friendsList;

  return (
    <div className="w-full h-screen relative bg-light-pattern dark:bg-[#0D0D0D] flex flex-col overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>

      <div 
        ref={scrollViewportRef} 
        onScroll={(e) => {
          const el = e.currentTarget;
          const top = el.scrollTop;
          setShowScrollToTop(top > 300);
          scrolledDownRef.current = top > 300;
          lastScrollTopRef.current = top;
          // Reached the top on their own → merge any pending new yaps and dismiss.
          if (top <= 4 && hasNewYaps) {
            setHasNewYaps(false);
            markNewestSeen(); // reached the top on their own — same as tapping the pill
            setLastRefreshTime(Date.now());
          }
          // Near the bottom → mount the next batch of cards, and once the
          // mounted cards have caught up with everything fetched, pull the next
          // page from Firestore so the feed keeps going.
          if (el.scrollHeight - (top + el.clientHeight) < 800) {
            setVisibleCount((c) => (c < displayYaps.length ? Math.min(displayYaps.length, c + RENDER_STEP) : c));
            if (feedPagingEnabled && hasMoreFeed && visibleCount >= displayYaps.length - RENDER_STEP) {
              void loadMoreFeed();
            }
          }
        }}
        data-slot="scroll-area-viewport" 
        className="flex-1 w-full overflow-y-auto min-h-0 overscroll-y-contain"
      >
        <div className="w-full max-w-2xl mx-auto relative z-10 pb-[150px]">
          {/* Header */}
          <div
            className="pb-4 px-6 border-b border-black/10 dark:border-white/10 bg-transparent"
            style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}
          >
            <div className="flex items-center justify-center mb-4 relative min-h-[40px]">
              {/* Logo and buttons on the left */}
              <div className="absolute left-0 flex items-center gap-3">
                <img src={String(theme === 'dark' ? logoImageWhite : logoImage)} alt="AfroYap" className="h-6 w-auto" />

                {/* Notifications */}
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    // Notifications is auth-gated — prompt instead of bouncing a
                    // logged-out visitor out of the feed and onto /login.
                    if (!isAuthed) { openAuthPrompt('Create an account to get notifications'); return; }
                    window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_SCREEN, { detail: SCREENS.NOTIFICATIONS }));
                  }}
                  className="relative p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                >
                  <Bell className="w-5 h-5 text-black dark:text-white" />
                  {unreadCount > 0 && (
                    <span className={`absolute -top-2 -left-2 ${unreadCount > 9 ? 'min-w-[26px] h-[22px] px-1' : 'w-5 h-5'} bg-[#E74C3C] dark:bg-[#3498DB] text-white text-[11px] flex items-center justify-center rounded-full border-2 border-white dark:border-[#0D0D0D] font-bold`}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </motion.button>
              </div>

              <h1 className="text-3xl text-black dark:text-white" style={{ fontFamily: "'NarnialOne', 'Narniaone', 'NARNIAONE', 'Poppins', sans-serif" }}>YapTab</h1>

              <div className="absolute right-0 flex items-center gap-2">
                {!isAuthed ? (
                  /* Logged-out visitor: the gold balance is meaningless without an
                     account, so the same slot carries the persistent way into the
                     login flow — reachable everywhere on the public feed without
                     being the forced first screen. */
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_SCREEN, { detail: SCREENS.LOGIN }))}
                    className="bg-brand-gradient text-white rounded-full shadow-lg tracking-widest"
                    style={{ padding: '7px 16px', fontSize: '11px', fontWeight: 800, letterSpacing: '0.12em' }}
                  >
                    LOG IN
                  </motion.button>
                ) : (
                  /* Market / Gold Display */
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    // onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'store' }))}
                    className="flex items-center gap-1.5 bg-black/5 dark:bg-white/5 px-3 py-1.5 rounded-full border border-black/10 dark:border-white/10 backdrop-blur-sm opacity-50 cursor-not-allowed"
                  >
                    <img src={goldIcon as any} alt="Gold" className="h-4 w-auto object-contain" />
                    <span className="text-black dark:text-white text-xs font-bold">{Number(userProfile.goldBalance || 0).toLocaleString()}</span>
                  </motion.button>
                )}
              </div>
            </div>

            {/* Tabs */}
            <Tabs 
              value={activeTab} 
              onValueChange={(val: string) => {
                const newTab = val as 'world' | 'friends';
                // Friends feed requires auth — prompt signup instead of switching.
                if (newTab === 'friends' && !requireAuth(undefined, 'Create an account to see your friends’ feed')) return;
                setActiveTab(newTab);
                onTabChange?.(newTab);
              }}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2 gap-2 bg-[#f2deb3] dark:bg-black/50 p-1 rounded-xl h-auto relative overflow-hidden">
                <TabsTrigger
                  value="world"
                  className="group data-[state=active]:text-white text-[#ea580c] dark:text-white rounded-lg transition-all tab-shadow h-10 overflow-hidden relative"
                >
                  <div 
                    className={`absolute inset-0 transition-opacity duration-300 ${activeTab === 'world' ? 'opacity-100' : 'opacity-0'}`}
                    style={{ 
                      background: theme === 'dark' 
                        ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' 
                        : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)' 
                    }}
                  />
                  <span className="relative z-10">World</span>
                </TabsTrigger>
                <TabsTrigger
                  value="friends"
                  className="group data-[state=active]:text-white text-[#ea580c] dark:text-white rounded-lg transition-all tab-shadow h-10 overflow-hidden relative"
                >
                  <div 
                    className={`absolute inset-0 transition-opacity duration-300 ${activeTab === 'friends' ? 'opacity-100' : 'opacity-0'}`}
                    style={{ 
                      background: theme === 'dark' 
                        ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' 
                        : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)' 
                    }}
                  />
                  <span className="relative z-10">Friends</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Search and Filter - Different for each tab */}
            {activeTab === 'world' ? (
              /* World Tab: Keyword Search and Filter */
              <div className="mt-3 flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999999]" />
                  <Input
                    placeholder="Keyword Search"
                    value={keywordSearch}
                    onChange={(e) => setKeywordSearch(e.target.value)}
                    className="pl-9 h-11 bg-[#f2deb3] dark:bg-[#1a1a1a] border-2 border-black/20 dark:border-white/20 text-[#ea580c] dark:text-white placeholder:text-[#666666] dark:placeholder:text-[#a0a0a0] rounded-xl text-sm"
                  />
                </div>

                {/* Side-by-Side Square Buttons */}
                <div className="flex items-center gap-2 flex-none">
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={handleManualRefresh}
                    className="w-11 h-11 aspect-square rounded-xl flex items-center justify-center text-white shadow-md border-2 border-black/10 dark:border-white/10 shrink-0"
                    style={{ 
                      background: theme === 'dark' 
                        ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' 
                        : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)' 
                    }}
                    title="Refresh feed"
                  >
                    <RefreshCw className={`w-5 h-5 text-white ${isLoading ? 'animate-spin' : ''}`} />
                  </motion.button>

                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowFilterModal(true)}
                    data-tour="guide-sorts"
                    className="w-11 h-11 aspect-square rounded-xl flex items-center justify-center text-white shadow-md button-shadow shrink-0"
                    style={{ 
                      background: theme === 'dark' 
                        ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' 
                        : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)' 
                    }}
                  >
                    <SlidersHorizontal className="w-5 h-5 text-white" />
                  </motion.button>
                </div>
              </div>
            ) : (
              /* Friends Tab: Always show search bar */
              <div className="mt-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999999]" />
                  <Input
                    placeholder="Search friends"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-10 bg-[#f2deb3] dark:bg-[#1a1a1a] border-2 border-black/20 dark:border-white/20 text-[#1a1a1a] dark:text-white placeholder:text-[#666666] dark:placeholder:text-[#a0a0a0] rounded-xl text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Friends Tab - Horizontal Friends */}
          {activeTab === 'friends' && (
            <div className="p-4 bg-transparent backdrop-blur-sm">
              {/* Horizontal circular row of friends */}
              {filteredFriendsList.length > 0 ? (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {filteredFriendsList.map((friend) => (
                    <motion.div
                      key={friend.id}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleFriendClick(friend)}
                      className="shrink-0 cursor-pointer"
                    >
                      <LegionAvatarWrapper rank={friend.rank} className="shadow-lg">
                        <Avatar className={`w-16 h-16 ${['legion'].includes(friend.rank as string) ? 'border-none' : (getRankRingParams(friend.rank).ringClass || 'border-2 border-[#4478ea]')}`}>
                          <AvatarImage src={UserService.resolveUserAvatar(friend)} />
                          <AvatarFallback>{friend.name[0]}</AvatarFallback>
                        </Avatar>
                      </LegionAvatarWrapper>
                      <p className="text-xs text-center text-[#ea580c] dark:text-white mt-1 truncate w-16">
                        {friend.name.split(' ')[0]}
                      </p>
                      {(() => {
                        // The strip column is 64px, too narrow for the full
                        // "Active this week", so the "Active" prefix is dropped
                        // here. Same buckets and same rule-gated read as the
                        // label elsewhere: nothing renders when it is hidden.
                        const label = activityLabel(friendLastActive[String(friend.id)] ?? null);
                        if (!label) return null;
                        const now = label === 'Active now';
                        const short = now ? 'Now' : label.replace(/^Active /, '').replace(/^\w/, c => c.toUpperCase());
                        return (
                          <p
                            style={{
                              width: 64, textAlign: 'center', fontSize: 10, lineHeight: 1.2, marginTop: 2,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              color: now ? '#22C55E' : '#8e8e8e', fontWeight: now ? 600 : 400,
                            }}
                          >
                            {short}
                          </p>
                        );
                      })()}
                    </motion.div>
                  ))}
                </div>
              ) : searchQuery.trim() && friendsList.length > 0 ? (
                <div className="text-center py-4">
                  <Search className="w-10 h-10 mx-auto mb-2 text-[#999999] dark:text-[#666666]" />
                  <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">No friends match "{searchQuery}"</p>
                </div>
              ) : (
                <div className="text-center py-4">
                  <UserPlus className="w-12 h-12 mx-auto mb-2 text-[#999999] dark:text-[#666666]" />
                  <p className="text-sm text-[#666666] dark:text-[#a0a0a0] mb-3">No friends yet</p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_SCREEN, { detail: SCREENS.DISCOVER }));
                      }}
                      className="px-4 py-2 rounded-xl text-white text-sm"
                      style={{ 
                        background: theme === 'dark' 
                          ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' 
                          : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)' 
                      }}
                    >
                      Find Friends
                    </motion.button>
                </div>
              )}
            </div>
          )}

          <div className="p-5 pb-56 space-y-6">
            {/* Composer is posting UI — hidden entirely for logged-out visitors
                rather than shown disabled, since nothing in it is usable until
                they have an account. */}
            {activeTab === 'world' && isAuthed && !recapMode && (
              <PromptComposer onClick={handleCreateYap} />
            )}

            {/* Says what this view is and how to leave it. Without a banner the
                reader has a differently-ordered feed and no explanation, which
                reads as the feed being broken rather than filtered. */}
            {recapMode && (
              <div
                className="flex items-center justify-between gap-3 mx-4 mb-3 px-4 py-3 rounded-xl"
                style={{
                  backgroundColor: theme === 'dark' ? 'rgba(68,120,234,0.12)' : 'rgba(243,156,18,0.12)',
                  border: `1px solid ${theme === 'dark' ? 'rgba(68,120,234,0.35)' : 'rgba(243,156,18,0.35)'}`,
                }}
              >
                <div className="min-w-0">
                  <p className="text-sm font-bold text-black dark:text-white">Busiest yaps</p>
                  <p className="text-xs text-[#666666] dark:text-[#a0a0a0]">
                    Most replies in the last 24 hours
                  </p>
                </div>
                {/* 44x44 to clear the minimum touch target. */}
                <button
                  type="button"
                  onClick={exitRecapMode}
                  aria-label="Back to your feed"
                  className="shrink-0 flex items-center justify-center rounded-full text-[#666666] dark:text-[#a0a0a0]"
                  style={{ width: 44, height: 44 }}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
            
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonYap key={i} />)
            ) : displayYaps.length === 0 && recapMode ? (
              // Distinct from the ordinary empty feed: nothing is wrong, the
              // window simply has nothing in it, and the way out is the point.
              <div className="flex flex-col items-center justify-center py-20 text-center px-6">
                <div className="w-20 h-20 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center mb-6">
                  <MessageCircle className="w-10 h-10 text-[#999999]/40" />
                </div>
                <p className="text-[#666666] dark:text-[#E5E5E5] text-lg font-medium">
                  No replies in the last 24 hours
                </p>
                <button
                  type="button"
                  onClick={exitRecapMode}
                  className="mt-4 px-4 py-2 rounded-xl text-sm font-bold text-[#4478ea]"
                >
                  Back to your feed
                </button>
              </div>
            ) : displayYaps.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center px-6">
                {activeTab === 'world' ? (
                  <>
                    <div className="w-20 h-20 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center mb-6">
                      <MessageCircle className="w-10 h-10 text-[#999999]/40" />
                    </div>
                    <p className="text-[#666666] dark:text-[#E5E5E5] text-lg font-medium">
                      {keywordSearch.trim().length > 1
                        ? `No Yaps found for "${keywordSearch}"`
                        : "No Yaps today? What are YOU waiting for?"}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="w-20 h-20 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center mb-6">
                      <span className="text-4xl">😴</span>
                    </div>
                    <p className="text-[#666666] dark:text-[#E5E5E5] text-lg font-semibold mb-2">
                      No Yaps yet
                    </p>
                    <p className="text-[#999999] dark:text-[#a0a0a0] text-sm">
                      Tell your friends to get active!
                    </p>
                  </>
                )}
              </div>
            ) : (
              <>
                {displayYaps.slice(0, visibleCount).map((yap: any, yapIndex: number) => {
                  const authorInfo = getYapAuthorInfo(yap, userProfile);
                  const yapAuthorId = (yap.shoutedByName || yap.type === 'shouted') ? yap.shoutedBy : (yap.userId || yap.authorId);
                  const isFriendAuthor = !yap.isAnonymous && !!yapAuthorId && String(yapAuthorId) !== String(userProfile?.id) && isFriend(String(yapAuthorId));
                  const interactionId = getYapInteractionId(yap);
                  const isQuote = isQuoteShout(yap);
                  const isShout = isShoutedYap(yap);

                  return (
                    <Fragment key={yap.id}>
                    {/* Trending runs out of "trending" long before it runs out
                        of yaps. This says so, once, at the boundary, so the
                        change in ordering below it reads as intentional rather
                        than as the feed losing its sort. */}
                    {yapIndex === olderYapsStartIndex && (
                      <div className="flex items-center gap-3 mx-4 my-4" aria-hidden={false}>
                        <div
                          className="flex-1"
                          style={{ height: 1, backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)' }}
                        />
                        <span
                          className="shrink-0 uppercase"
                          style={{
                            fontSize: 11,
                            letterSpacing: '0.08em',
                            fontWeight: 700,
                            color: theme === 'dark' ? 'rgba(255,255,255,0.40)' : 'rgba(0,0,0,0.40)',
                          }}
                        >
                          Older Yaps
                        </span>
                        <div
                          className="flex-1"
                          style={{ height: 1, backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)' }}
                        />
                      </div>
                    )}
                    <PressableCard
                      data-yap-id={yap.id}
                      data-yap-ts={new Date((yap as any).sortTime || (yap as any).time || (yap as any).timestamp || 0).getTime()}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-5 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/5 dark:border-white/10 card-shadow"
                      onClick={() => handleYapClick(yap)}
                    >
                      <div>
                        <div className="flex flex-col gap-3">
                          {/* Header Row: Avatar, Name, Flag, Location, Menu */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 min-w-0 flex-1">
                              {yap.isAnonymous ? (
                                <AnonymousAvatar className="w-10 h-10 shadow-sm shrink-0" size="sm" />
                              ) : (
                                <Avatar
                                  className="w-10 h-10 shadow-sm shrink-0 cursor-pointer"
                                  onClick={(e: React.MouseEvent) => handleAuthorClick(yap, authorInfo, yapAuthorId, e)}
                                >
                                  <AvatarImage src={authorInfo.avatar} />
                                  <AvatarFallback>{authorInfo.name[0] || '?'}</AvatarFallback>
                                </Avatar>
                              )}
                              <div
                                className={`flex flex-col min-w-0 ${yap.isAnonymous ? '' : 'cursor-pointer'}`}
                                onClick={(e: React.MouseEvent) => handleAuthorClick(yap, authorInfo, yapAuthorId, e)}
                              >
                                <div className="flex items-center gap-1.5">
                                  <span className="font-bold text-black dark:text-white truncate">{yap.isAnonymous ? 'Anonymous' : authorInfo.name}</span>
                                  {!yap.isAnonymous && authorInfo.rank && (
                                    <RankBadge rank={authorInfo.rank} size={18} />
                                  )}
                                  {!yap.isAnonymous && (
                                    <>
                                      <span className="text-[11px] opacity-40 shrink-0">&bull;</span>
                                      <FlagImage
                                        countryName={authorInfo.country}
                                        size="w80"
                                        className="w-4 aspect-3/2 object-contain rounded shrink-0"
                                      />
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-[#666666] dark:text-[#a0a0a0] opacity-80 mt-0.5 min-w-0">
                                  <span className="truncate flex-1 min-w-0">{yap.isAnonymous ? ANONYMOUS_USER.username : authorInfo.username}</span>
                                  {(yap.visibility === 'friends' || yap.friendsOnly === true) && (
                                    <>
                                      <span className="text-[11px] opacity-40 shrink-0">•</span>
                                      <div className="flex items-center gap-1 text-[#E74C3C] dark:text-[#4478ea] shrink-0 drop-shadow-md opacity-100">
                                        <Lock className="w-3 h-3" />
                                        <span className="text-[11px] uppercase font-bold">Friends Only</span>
                                      </div>
                                    </>
                                  )}
                                </div>
                                {isFriendAuthor && (
                                  <div className="mt-0.5">
                                    <FriendBadge />
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-3 shrink-0">
                              {!yap.isAnonymous && authorInfo.residency && (
                                <div className="flex items-center gap-1 text-[#666666] dark:text-[#a0a0a0]">
                                  <MapPin style={{ width: '12px', height: '12px' }} className="text-[#4478ea] shrink-0" />
                                  <span className="text-xs font-medium">{authorInfo.residency}</span>
                                </div>
                              )}
                              
                              {/* Shared menu — see ui/PostMenu. The 275-line
                                  hand-written list that used to be here is what
                                  made the feed's options differ from the detail
                                  screen's for the same post. */}
                              <PostMenu
                                post={yap as any}
                                surface="feed"
                                collectionName={(yap as any)?.isShout ? 'shouts' : 'yaps'}
                                onViewProfile={() => handleAuthorClick(yap, authorInfo, yapAuthorId)}
                                onShare={() => handleShareClick(yap, { stopPropagation: () => {} } as any)}
                                onEditRequested={() => setEditingYap(yap)}
                                onDeleteRequested={() => handleDeleteYapClick(yap, { stopPropagation: () => {} } as any)}
                                onSeeLikes={() => setLikers({
                                  title: 'Liked your yap',
                                  load: () => YapService.getYapLikers(yap.id, (userProfile as any)?.ageGroup),
                                })}
                                onChanged={() => setLastRefreshTime(Date.now())}
                              />
                            </div>
                          </div>

                          {/* Pinned badge */}
                          {isPinActive(yap) && (
                            <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-[#F39C12]/10 dark:bg-[#4478ea]/10 border border-[#F39C12]/30 dark:border-[#4478ea]/30 w-fit mb-1">
                              <Pin className="w-3 h-3 text-[#F39C12] dark:text-[#4478ea] fill-[#F39C12] dark:fill-[#4478ea]" />
                              <span className="text-[11px] font-bold text-[#F39C12] dark:text-[#4478ea]">
                                Pinned by Legion
                              </span>
                            </div>
                          )}

                          {/* Content Section: Directly under Header Row */}
                          <div className="flex flex-col gap-2">
                            {isShout && (
                              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[#4478ea] dark:text-[#4478ea]">
                                <Megaphone className="w-3.5 h-3.5 fill-[#4478ea] dark:fill-[#4478ea]" />
                                <span>Shouted</span>
                              </div>
                            )}

                            {isShout ? (
                              <>
                                {(yap as any).shoutCaption && (
                                  <>
                                    <FormattedText 
                                      text={(yap as any).shoutCaption} 
                                      className="text-[#666666] dark:text-[#E5E5E5] text-[15px] leading-[1.4] whitespace-pre-wrap wrap-break-word block" 
                                    />
                                    
                                    {extractFirstUrl((yap as any).shoutCaption) && (
                                      <LinkPreview url={extractFirstUrl((yap as any).shoutCaption)!} />
                                    )}
                                  </>
                                )}

                                {isQuote && (yap as any).shoutImages && (yap as any).shoutImages.length > 0 && (
                                  <div className={`mt-1 ${(yap as any).shoutImages.length >= 2 ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-1'}`}>
                                    {(yap as any).shoutImages.map((img: string, idx: number) => (
                                      <div key={idx} className="rounded-xl overflow-hidden border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5 w-full">
                                        <ImageWithPreview src={img} alt={`Shout image ${idx + 1}`} className="w-full h-auto" />
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Voice Bubble (Shout Audio) */}
                                {(yap as any).audioUrl && (
                                  <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                                    <VoiceReplyBubble 
                                      audioUrl={(yap as any).audioUrl} 
                                      duration={(yap as any).duration}
                                      className="w-full"
                                    />
                                  </div>
                                )}

                                {(yap as any).originalYap && (
                                  <div className="mt-1">
                                    <EmbeddedYap
                                      suppressLinkPreview={!!extractFirstUrl((yap as any).shoutCaption)}
                                      yapId={getOriginalYapId(yap)}
                                      username={(yap as any).originalYap.username || (yap as any).originalYap.userUsername || '@unknown'}
                                      authorName={(yap as any).originalYap.author || (yap as any).originalYap.userName || 'Unknown'}
                                      avatar={(yap as any).originalYap.avatar || (yap as any).originalYap.userAvatar || ''}
                                      country={(yap as any).originalYap.countryFlag || (yap as any).originalYap.country}
                                      timestamp={(yap as any).originalYap.timestamp || (yap as any).originalYap.time || ''}
                                      content={(yap as any).originalYap.content || ''}
                                      images={(yap as any).originalYap.images || ((yap as any).originalYap.image ? [(yap as any).originalYap.image] : [])}
                                      image={(yap as any).originalYap.image}
                                      likes={(yap as any).originalYap.likes || 0}
                                      comments={(yap as any).originalYap.comments || 0}
                                      shouts={(yap as any).originalYap.shouts || 0}
                                      isLiked={isYapLiked(getOriginalYapId(yap))}
                                      isShout={true}
                                      isDeleted={(yap as any).originalYap.deleted}
                                      showActions={false}
                                      audioUrl={(yap as any).originalYap.audioUrl}
                                      duration={(yap as any).originalYap.duration}
                                      onYapClick={(e) => {
                                        e?.stopPropagation();
                                        window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_YAP_DETAIL, { detail: (yap as any).originalYap }));
                                      }}
                                      maxLines={3}
                                    />
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <ExpandableText
                                  text={yap.content || ''}
                                  className="text-[#666666] dark:text-[#E5E5E5] text-[15px] leading-[1.4] whitespace-pre-wrap wrap-break-word block"
                                />
                                
                                {extractFirstUrl(yap.content) && (
                                  <LinkPreview url={extractFirstUrl(yap.content)!} />
                                )}

                                {yap.forumTopic?.forumId && (
                                  <div className="my-2">
                                    <YapTopicLabel forumId={yap.forumTopic.forumId} forumName={yap.forumTopic.forumName} />
                                  </div>
                                )}

                                {(yap as any).roomShare?.roomId && (
                                  <RoomShareCard
                                    roomId={(yap as any).roomShare.roomId}
                                    title={(yap as any).roomShare.title}
                                  />
                                )}

                                {(yap as any).poll?.options?.length > 0 && (
                                  <PollCard
                                    yapId={String(yap.id)}
                                    poll={(yap as any).poll}
                                    readOnly={!isAuthed}
                                    onBeforeVote={() => requireAuth(undefined, 'Create an account to vote')}
                                  />
                                )}

                                {yap.audioUrl && (
                                  <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                                    <VoiceReplyBubble 
                                      audioUrl={yap.audioUrl} 
                                      duration={yap.duration}
                                      className="w-full"
                                    />
                                  </div>
                                )}

                                {(yap.image || (yap.images && yap.images.length > 0)) && (
                                  <YapImageGrid
                                    images={yap.images && yap.images.length > 0 ? yap.images : (yap.image ? [yap.image] : [])}
                                  />
                                )}

                                {(yap as any).videoUrl && (
                                  <YapVideoPlayer
                                    videoUrl={(yap as any).videoUrl}
                                    trimStart={(yap as any).videoTrimStart}
                                    trimEnd={(yap as any).videoTrimEnd}
                                    videoWidth={(yap as any).videoWidth}
                                    videoHeight={(yap as any).videoHeight}
                                    authorName={yap.isAnonymous ? 'Anonymous' : authorInfo.name}
                                  />
                                )}
                              </>
                            )}
                          </div>

                          {/* Interaction Buttons - Left Aligned with Content */}
                          <div className="flex items-center gap-3 mt-1">
                            <LikeButton
                              postId={interactionId}
                              postType={getYapPostType(yap)}
                              originalLikeCount={yap.likes || 0}
                              onBeforeToggle={() => markYapSeen(yap)}
                              canInteract={() => isAuthed && canInteractWithPost(yap)}
                              onBlockedInteraction={() => {
                                if (!isAuthed) { openAuthPrompt('Create an account to like posts'); return; }
                                const m = groupPostBlockMsg(yap);
                                toast.error(m.title, { description: m.desc });
                              }}
                              className="mr-2"
                            />

                            {!yap.isShout && canShareOrShout(yap) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!canInteractWithPost(yap)) {
                                    const m = groupPostBlockMsg(yap);
                                    toast.error(m.title, { description: m.desc });
                                    return;
                                  }
                                  handleToggleShout(yap, e);
                                }}
                                className={`flex items-center gap-2 h-auto px-0 ${!canInteractWithPost(yap) ? 'opacity-50' : isYapShouted(interactionId) ? 'text-[#F39C12] dark:text-[#4478ea]' : 'text-[#666666] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea]'}`}
                              >
                                <Megaphone className={`w-4 h-4 ${isYapShouted(interactionId) ? 'fill-[#F39C12] dark:fill-[#4478ea]' : ''}`} />
                                <span className="text-sm font-medium">{getShoutCount(yap.shouts || 0, interactionId)}</span>
                              </Button>
                            )}

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!canInteractWithPost(yap)) {
                                  const m = groupPostBlockMsg(yap);
                                  toast.error(m.title, { description: m.desc });
                                  return;
                                }
                                handleYapClick(yap);
                              }}
                              className={`flex items-center gap-2 h-auto px-0 ${!canInteractWithPost(yap) ? 'opacity-50' : 'text-[#666666] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea]'}`}
                            >
                              <MessageCircle className="w-4 h-4" />
                              <span className="text-sm font-medium">{getPostReplyCount(yap.comments || 0, interactionId, getYapPostType(yap))}</span>
                            </Button>

                            {canShareOrShout(yap) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!canInteractWithPost(yap)) {
                                    const m = groupPostBlockMsg(yap);
                                    toast.error(m.title, { description: m.desc });
                                    return;
                                  }
                                  handleShareClick(yap, e);
                                }}
                                className={`flex items-center gap-2 h-auto px-0 ${!canInteractWithPost(yap) ? 'opacity-50' : 'text-[#666666] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea]'}`}
                              >
                                <Share2 className="w-4 h-4" />
                              </Button>
                            )}
                            <span className="ml-auto min-w-max text-xs text-[#999999] dark:text-[#808080] font-medium shrink-0">
                              {formatTimeAgo((yap as any).sortTime || (yap as any).time || 'Now')}
                              {/* Edits are shown, not silent: a post rewritten
                                  after people replied to it should say so. */}
                              {(yap as any).edited ? ' · edited' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                    </PressableCard>
                    </Fragment>
                  );
                })}

                {/* Incremental loader: more cards mount as this scrolls into
                    view, and older pages are fetched once they run out. */}
                {!showPreviewWall && (visibleCount < displayYaps.length || isLoadingMore || (feedPagingEnabled && hasMoreFeed)) && (
                  <div className="flex justify-center py-6">
                    <div className="w-6 h-6 rounded-full border-2 border-[#4478ea]/30 border-t-[#4478ea] animate-spin" />
                  </div>
                )}

                {/* True end of the feed — everything since the cutoff is loaded. */}
                {feedPagingEnabled && !hasMoreFeed && !isLoadingMore && displayYaps.length > 0 && visibleCount >= displayYaps.length && (
                  <div className="py-6 text-center text-xs text-[#999999] dark:text-[#808080]">
                    You’ve reached the beginning.
                  </div>
                )}

                {/* Public preview wall: logged-out visitors hit this after the
                    first {PUBLIC_PREVIEW_LIMIT} yaps and must create an account to continue. */}
                {showPreviewWall && (
                  <div className="mx-4 my-6 p-6 rounded-2xl bg-white/70 dark:bg-white/5 border border-black/10 dark:border-white/10 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-brand-gradient flex items-center justify-center">
                      <Lock className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-black dark:text-white mb-1">Create a free account to continue</h3>
                    <p className="text-sm text-[#666666] dark:text-[#999999] mb-4">
                      You’ve reached the end of the preview. Sign up to keep scrolling and to like, reply, and post.
                    </p>
                    <button
                      onClick={() => openAuthPrompt('Create a free account to continue')}
                      className="px-6 py-3 rounded-xl bg-brand-gradient text-white font-bold active:scale-95 transition-transform"
                    >
                      Create account
                    </button>
                  </div>
                )}

                {/* Visual Spacer to push content past bottom nav and Floating Action Buttons */}
                <div className="h-48 shrink-0 w-full" aria-hidden="true" />
              </>
            )}
          </div>
        </div>

        {/* Yap Filter Modal */}
        <YapFilterModal
          isOpen={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          onApplyFilters={(sortBy) => setActiveSort(sortBy)}
          currentSort={activeSort}
        />

        {/* Scroll to Top Button */}
        {createPortal(
          <AnimatePresence>
            {showScrollToTop && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 20 }}
                style={{
                  position: 'fixed',
                  bottom: '165px',
                  right: '16px',
                  zIndex: 45, // Above timer (40) but below modals (50)
                  pointerEvents: 'auto',
                }}
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={scrollToTop}
                  className="w-11 h-11 aspect-square rounded-full flex items-center justify-center text-white backdrop-blur-md"
                  style={{
                    background: theme === 'dark'
                      ? 'linear-gradient(to right, #3b82f6, #2563eb)'
                      : 'linear-gradient(to right, #F39C12, #E74C3C)',
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  <ArrowUp className="w-5 h-5 text-white" />
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

        {/* "New Yaps Dropped!" pill — slides in from the top when newer yaps
            arrived while the user is scrolled down. Blue gradient/shape reused
            from the (hidden) YapDay timer for visual consistency. */}
        {createPortal(
          <AnimatePresence>
            {hasNewYaps && activeTab === 'world' && (
              <motion.button
                key="new-yaps-pill"
                initial={{ opacity: 0, y: -48 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -48 }}
                transition={{ type: 'spring', damping: 20, stiffness: 260 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNewYapsTap}
                className="fixed h-11 rounded-full flex items-center gap-3 text-white font-bold backdrop-blur-md whitespace-nowrap"
                style={{
                  // Padding is inline because `px-*` utilities in this range have no
                  // rule in the shipped stylesheet (see note in LoginPromptModal) —
                  // the old px-7 was a no-op, which is why the pill sat tight
                  // against its text.
                  paddingLeft: '28px',
                  paddingRight: '28px',
                  // Horizontally centred with auto margins against both edges,
                  // NOT a translateX: motion owns the transform on this element
                  // to animate the slide, so a transform-based centring gets
                  // overwritten the moment the animation runs, which left the
                  // pill sitting off to the right.
                  left: 0,
                  right: 0,
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  width: 'fit-content',
                  // Parked at the top, clear of the notch. The pill only appears
                  // once the user has scrolled down, so the header it would
                  // otherwise cover has already scrolled away.
                  top: 'max(16px, calc(env(safe-area-inset-top) + 12px))',
                  zIndex: 46, // above scroll-to-top (45), below modals (50)
                  background: 'linear-gradient(to right, #3b82f6, #2563eb)',
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.5)',
                  pointerEvents: 'auto',
                }}
              >
                <ArrowUp className="w-4 h-4 text-white" />
                <span className="text-sm">New Yaps Dropped!</span>
              </motion.button>
            )}
          </AnimatePresence>,
          document.body
        )}

        <CreateYapModal
          isOpen={showCreateYap}
          onClose={() => setShowCreateYap(false)}
          visibility={activeTab === 'friends' ? 'friends' : 'public'}
          groupId={groupId}
          groupType={groupType}
        />
        {
          selectedYapToShare && (
            <ShareYapModal
              isOpen={showShareYap}
              onClose={() => {
                setShowShareYap(false);
                setSelectedYapToShare(null);
              }}
              yap={selectedYapToShare}
            />
          )
        }
        {
          selectedYapToShout && (
            <ShoutYapModal
              isOpen={showShoutModal}
              onClose={() => {
                setShowShoutModal(false);
                setSelectedYapToShout(null);
              }}
              yap={selectedYapToShout}
              onShoutWithCaption={handleShoutWithCaption}
              onShoutWithoutCaption={handleShoutWithoutCaption}
            />
          )
        }
        {
          selectedYapToShout && (
            <ShoutedYapOptionsModal
              isOpen={showShoutOptionsModal}
              onClose={() => {
                setShowShoutOptionsModal(false);
                setSelectedYapToShout(null);
              }}
              yap={selectedYapToShout}
              onDeleteCaption={handleDeleteCaption}
              onDeleteShout={handleDeleteShout}
              onShare={() => {
                setShowShoutOptionsModal(false);
                // Find the shouted yap to share
                const shoutedYap = shoutedYaps.find(shout => shout.originalYap.id === selectedYapToShout.id);
                if (shoutedYap) {
                  setSelectedShoutToShare(shoutedYap);
                  setShowShareShout(true);
                }
              }}
            />
          )
        }
        {
          selectedShoutToShare && (
            <ShareShoutModal
              isOpen={showShareShout}
              onClose={() => {
                setShowShareShout(false);
                setSelectedShoutToShare(null);
              }}
              shout={selectedShoutToShare}
            />
          )
        }

        {/* Shared with replies and the detail screen — one edit surface. */}

        <EditPostModal

          open={!!editingYap}

          onClose={() => setEditingYap(null)}

          postId={String(editingYap?.id || '')}

          initialContent={String(editingYap?.content || '')}

          timestamp={editingYap?.timestamp}

          onSaved={(next) => {

            // Patch in place so the feed shows the edit immediately rather

            // than after a refetch.

            setAllYaps((prev: any[]) => prev.map((y: any) =>

              String(y.id) === String(editingYap?.id) ? { ...y, content: next, edited: true } : y));

          }}

        />

        <DeleteYapModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          isDeleting={isDeleting}
          isSelfDelete={!!yapToDelete && String(yapToDelete.userId) === String(userProfile.id)}
        />

        <LikersModal
          open={!!likers}
          onClose={() => setLikers(null)}
          loadUserIds={likers?.load}
          title={likers?.title}
        />

        <LegionDeleteModal
          isOpen={!!legionDeleteTarget}
          onClose={() => setLegionDeleteTarget(null)}
          yapId={legionDeleteTarget?.yapId || ''}
          yapContent={legionDeleteTarget?.content || ''}
          authorId={legionDeleteTarget?.authorId || ''}
          moderatorId={String(userProfile.id)}
          onDeleted={() => {
            setLegionDeleteTarget(null);
            setLastRefreshTime(Date.now());
          }}
        />
      </div>
    </div>
  );
}
